import React, { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, Paper, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { CopyBlock, dracula, atomOneDark, nord } from "react-code-blocks";
import { ReactNativeSDKAccountDiscovery, ReactNativeSDKAccountLinking, ReactNativeSDKAuthentication, ReactNativeSDKConsentManagement, ReactNativeSDKFAQSection, ReactNativeSDKFIPManagement, ReactNativeSDKInitialisationAndConnectionManagement, ReactNativeSDKInstallation, ReactNativeSDKLatestVersion, ReactNativeSDKPrerequisites, ReactNativeSDKScriptURLLoadingFAQ } from './reactNativeIntegrationComponents';
import { AndroidEventTracking, iOSEventTracking, FlutterEventTracking, ReactNativeEventTracking } from './mobileEventTrackingComponents';
import BackendResponseCodeMapping from '../BackendResponseCodeMapping';

const MobileSDKIntegration: React.FC = React.memo(() => {
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedTab, setSelectedTab] = useState(0);
    const [selectedPlatform, setSelectedPlatform] = useState('android');
    const [selectedEventPlatform, setSelectedEventPlatform] = useState('android');

    const getThemeForPlatform = (platform: string) => {
        switch (platform) {
            case 'android':
                return nord;  // Dark theme with green accents
            case 'ios':
                return dracula;  // Dark theme with blue accents
            case 'flutter':
                return atomOneDark;  // Light theme with good contrast
            case 'React-Native':
                return atomOneDark;
        }
    };

    const handlePlatformChange = (platform: string) => {
        setSelectedPlatform(platform);
    };

    const handleEventPlatformChange = (platform: string) => {
        setSelectedEventPlatform(platform);
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, [location.pathname]);

    useEffect(() => {
        if (location.hash === "#event-tracking") {
            setSelectedTab(1);
        } else {
            setSelectedTab(0);
        }
    }, [location.hash]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setSelectedTab(newValue);
        navigate(
            `/mobile-sdk-integration${newValue === 1 ? "#event-tracking" : "#sdk"}`,
        );
    };

    const sslCertificatePinningInfoComponent =
        <>
            <Typography sx={{ mb: 2, color: '#555', fontStyle: 'italic' }}>
                <strong>Note:</strong> The `certificatePins` parameter is optional. If you do not require SSL pinning, you can pass an empty list.
            </Typography>
            <Typography sx={{ mb: 2, color: '#555', fontStyle: 'italic' }}>
                To generate SSL certificate pins, follow these steps:
                <ol>
                    <li>Go to <a href="https://www.ssllabs.com/ssltest/analyze.html" target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'underline' }}>SSL Labs</a>.</li>
                    <li>Enter the WebSocket connection URL (without protocols, e.g., <code>webvwdev.finvu.in</code>) in the hostname field and hit submit.</li>
                    <li>Once the analysis is 100% complete, locate the <code>pin256</code> values under certificates #1 and #2.</li>
                    <li>Copy the strings after <code>pin256</code> (excluding the "pin256" text).</li>
                    <li>Use these strings as your certificate pins.</li>
                </ol>
                Example: From <code>Pin SHA256: TmZriS3UEzT3t5s8SJATgFdUH/llYL8vieP2wOuBAB8=</code>, extract <code>TmZriS3UEzT3t5s8SJATgFdUH/llYL8vieP2wOuBAB8=</code>.
            </Typography>
        </>;

    const SDKContent = () => (
        <Box>
            {/* Introduction */}
            <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                Welcome to the integration guide for Finvu Mobile SDK! This document provides detailed instructions
                on integrating our SDK into your mobile application.
                Below is the sequence diagram which includes SDK initialization, account linking and data fetch flows.
            </Typography>

            {/* SDK Flow Diagram */}

            <Box sx={{ mt: 2, mb: 2 }}>
                <img
                    src="/images/finvu-sdk-flow.png"
                    alt="Finvu SDK Flow Diagram"
                    style={{
                        width: '100%',
                        maxWidth: '800px',
                        height: 'auto',
                        display: 'block',
                        margin: '0 auto'
                    }}
                />
            </Box>

            {/* Development Guidelines Section */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                AA journey Development Guidelines
            </Typography>

            <Box sx={{ mt: 2 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    <Box sx={{ p: 2 }}>
                        {/* UX Guidelines */}
                        <Typography variant="h6" sx={{ color: "#0b3452", fontWeight: "bold" }}>
                            UX AA Sahamati Guidelines
                        </Typography>
                        <Typography sx={{ mt: 2, mb: 3, color: '#555' }}>
                            While developing AA screens, please follow the UX AA guidelines by referring to the {' '}
                            <a
                                href="https://workdrive.zohopublic.in/external/sheet/e0c838a03871e6258f44ff5b62042f2b89817a37c027e035c01e7d5ed9ce338f"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#0066cc', textDecoration: 'underline' }}
                            >
                                Sahamati Guidelines
                            </a>
                        </Typography>

                        {/* Code Guidelines */}
                        <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                            Code Guidelines
                        </Typography>

                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" sx={{ color: "#0b3452", fontWeight: "bold" }}>
                                1. Avoid Third-Party Imports, Code, or API Requests in the AA Journey
                            </Typography>
                            <Typography sx={{ mt: 1, mb: 3, color: '#555' }}>
                                In the AA journey screens, ensure that <strong>only AA flow-related code</strong> is present.
                                This means no third-party API requests that do not directly relate to the AA journey.
                            </Typography>

                            <Typography variant="subtitle1" sx={{ color: "#0b3452", fontWeight: "bold" }}>
                                2. Do Not Store Data in Device Local Storage or Local DB
                            </Typography>
                            <Typography sx={{ mt: 1, mb: 3, color: '#555' }}>
                                Avoid storing data in any local storage mechanisms like shared_preferences, flutter_secure_storage,
                                or local databases (e.g., SQLite) in the AA screens.
                            </Typography>

                            <Typography variant="subtitle1" sx={{ color: "#0b3452", fontWeight: "bold" }}>
                                3. Clean Data and Instances at the End of the AA Journey
                            </Typography>
                            <Typography sx={{ mt: 1, mb: 3, color: '#555' }}>
                                Ensure that all data is cleaned up when the AA journey ends. This includes closing any BLoC states
                                and cleaning up objects like arrays or any other data that should not persist.
                            </Typography>

                            <Typography variant="subtitle1" sx={{ color: "#0b3452", fontWeight: "bold" }}>
                                4. Avoid Redundant Calls for finvuManager Methods
                            </Typography>
                            <Typography sx={{ mt: 1, mb: 3, color: '#555' }}>
                                Repeatedly calling the same finvuManager methods can lead to unnecessary network requests, affecting
                                performance and efficiency. Instead, use a global state to minimize redundant calls and optimize
                                resource usage.
                            </Typography>

                            <Typography variant="subtitle1" sx={{ color: "#0b3452", fontWeight: "bold" }}>
                                5. Logout and Disconnect When the User Exits the AA Journey
                            </Typography>
                            <Typography sx={{ mt: 1, mb: 3, color: '#555' }}>
                                Ensure that when the user exits the AA journey—whether in success, failure, or opt-out—you call
                                the finvuManager.logout() and finvuManager.disconnect() methods to properly log out and disconnect
                                from the WebSocket.
                            </Typography>
                        </Box>

                        {/* Co-Creation Guidelines */}
                        <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                            Co-Creation Code Push to Repo
                        </Typography>
                        <Typography sx={{ mt: 2, mb: 3, color: '#555' }}>
                            We will create a GitHub repository named <code>fiu_journey_fiuname</code> and share it with you for
                            pushing your AA journey code for review.
                        </Typography>

                        {/* Code Review Process */}
                        <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                            Code Review Process
                        </Typography>
                        <Typography sx={{ mt: 2, mb: 3, color: '#555' }}>
                            Please create a feature branch from main always, once code is developed raise a PR for main with a
                            wrapper around the AA journey so we can test and add us the reviewers. Also attach screen recording
                            of the AA journey screens from your mobile app for both iOS and Android platforms in PR Description.
                        </Typography>

                        {/* UI Review Process */}
                        <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                            UI Review Process
                        </Typography>
                        <Typography sx={{ mt: 2, color: '#555' }}>
                            For each pull request created in the GitHub co-creation repository, please attach a screen recording
                            of the AA journey screens from your mobile app for both iOS and Android platforms, where you are
                            integrating the Finvu SDK. Additionally, kindly share these screen recordings in the designated
                            communication channels (e.g., WhatsApp) for review.
                        </Typography>
                    </Box>
                </Paper>
            </Box>

            {/* SDK Version Info */}
            <Typography variant="h6" sx={{ mt: 4, mb: 3, color: "#0b3452", fontWeight: "bold" }}>
                Latest SDK Versions
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
                <Box sx={{
                    flex: '1 1 auto',
                    minWidth: '200px',
                    p: 2,
                    bgcolor: 'rgba(11, 52, 82, 0.05)',
                    borderRadius: '4px',
                    border: '1px solid rgba(11, 52, 82, 0.1)'
                }}>
                    <Typography sx={{ color: '#0b3452', fontWeight: 'bold', mb: 1 }}>
                        Flutter SDK
                    </Typography>
                    <Typography sx={{ color: '#555' }}>
                        Latest Version: <code>1.0.8</code>
                    </Typography>
                </Box>

                <Box sx={{
                    flex: '1 1 auto',
                    minWidth: '200px',
                    p: 2,
                    bgcolor: 'rgba(11, 52, 82, 0.05)',
                    borderRadius: '4px',
                    border: '1px solid rgba(11, 52, 82, 0.1)'
                }}>
                    <Typography sx={{ color: '#0b3452', fontWeight: 'bold', mb: 1 }}>
                        Android SDK
                    </Typography>
                    <Typography sx={{ color: '#555' }}>
                        Client SDK: <code>1.1.0</code>
                    </Typography>
                    <Typography sx={{ color: '#555', mt: 1 }}>
                        Core SDK: <code>1.0.9</code>
                    </Typography>
                </Box>

                <Box sx={{
                    flex: '1 1 auto',
                    minWidth: '200px',
                    p: 2,
                    bgcolor: 'rgba(11, 52, 82, 0.05)',
                    borderRadius: '4px',
                    border: '1px solid rgba(11, 52, 82, 0.1)'
                }}>
                    <Typography sx={{ color: '#0b3452', fontWeight: 'bold', mb: 1 }}>
                        IOS SDK
                    </Typography>
                    <Typography sx={{ color: '#555' }}>
                        Latest Version: <code>1.1.1</code>
                    </Typography>
                </Box>

                <ReactNativeSDKLatestVersion />
            </Box>

            <Typography sx={{ my: 2, color: "#555", lineHeight: 1.7 }}>
                Choose your platform below to get started.
            </Typography>

            {/* Prerequisites Section */}
            <Typography variant="h6" sx={{ mt: 2, color: "#0b3452", fontWeight: "bold" }}>
                Prerequisites
            </Typography>

            {/* Platform Selector with Prerequisites */}
            <Box sx={{ mt: 3 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    {/* Platform Selector */}
                    <Box sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.12)',
                        display: 'flex',
                        gap: 1,
                        p: 1
                    }}>
                        {['Android', 'iOS', 'Flutter', 'React-Native'].map((platform) => (
                            <Typography
                                key={platform.toLowerCase()}
                                onClick={() => handlePlatformChange(platform.toLowerCase())}
                                sx={{
                                    px: 3,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem',
                                    fontWeight: selectedPlatform === platform.toLowerCase() ? 600 : 400,
                                    color: selectedPlatform === platform.toLowerCase() ? '#0b3452' : '#666',
                                    bgcolor: selectedPlatform === platform.toLowerCase() ? 'rgba(11, 52, 82, 0.08)' : 'transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: 'rgba(11, 52, 82, 0.05)',
                                        color: '#0b3452'
                                    }
                                }}
                            >
                                {platform}
                            </Typography>
                        ))}
                    </Box>

                    {/* Prerequisites Content */}
                    <Box sx={{ p: 2 }}>
                        {selectedPlatform === 'android' && (
                            <Box component="ul" sx={{ m: 0, pl: 3 }}>
                                <li>
                                    <Typography sx={{ lineHeight: 1.7 }}>
                                        Minimum SDK version 25
                                    </Typography>
                                    <Typography sx={{ lineHeight: 1.7 }}>
                                        target SDK version 35
                                    </Typography>
                                </li>
                                <li>
                                    <Typography sx={{ lineHeight: 1.7 }}>
                                        Minimum Kotlin version 1.9.0
                                    </Typography>
                                </li>
                            </Box>
                        )}
                        {selectedPlatform === 'ios' && (
                            <Box component="ul" sx={{ m: 0, pl: 3 }}>
                                <li>
                                    <Typography sx={{ lineHeight: 1.7 }}>
                                        Minimum iOS version 16
                                    </Typography>
                                </li>
                            </Box>
                        )}
                        {selectedPlatform === 'flutter' && (
                            <Box component="ul" sx={{ m: 0, pl: 3 }}>
                                <li>
                                    <Typography sx={{ lineHeight: 1.7 }}>
                                        Dart SDK version ≥3.3.0 &lt;4.0.0
                                    </Typography>
                                </li>
                                <li>
                                    <Typography sx={{ lineHeight: 1.7 }}>
                                        Flutter version ≥3.3.0
                                    </Typography>
                                </li>
                                <li>
                                    <Typography sx={{ lineHeight: 1.7 }}>
                                        Android : Minimum SDK version 25
                                    </Typography>
                                    <Typography sx={{ lineHeight: 1.7 }}>
                                        Android : target SDK version 35
                                    </Typography>
                                </li>
                                <li>
                                    <Typography sx={{ lineHeight: 1.7 }}>
                                        iOS: Minimum iOS version 16
                                    </Typography>
                                </li>
                            </Box>
                        )}

                        {selectedPlatform === 'react-native' && <ReactNativeSDKPrerequisites />}
                    </Box>
                </Paper>
            </Box>

            {/* Installation Section */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Installation
            </Typography>

            <Box sx={{ mt: 3 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    {/* Platform Selector */}
                    <Box sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.12)',
                        display: 'flex',
                        gap: 1,
                        p: 1
                    }}>
                        {['Android', 'iOS', 'Flutter', 'React-Native'].map((platform) => (
                            <Typography
                                key={platform.toLowerCase()}
                                onClick={() => handlePlatformChange(platform.toLowerCase())}
                                sx={{
                                    px: 3,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem',
                                    fontWeight: selectedPlatform === platform.toLowerCase() ? 600 : 400,
                                    color: selectedPlatform === platform.toLowerCase() ? '#0b3452' : '#666',
                                    bgcolor: selectedPlatform === platform.toLowerCase() ? 'rgba(11, 52, 82, 0.08)' : 'transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: 'rgba(11, 52, 82, 0.05)',
                                        color: '#0b3452'
                                    }
                                }}
                            >
                                {platform}
                            </Typography>
                        ))}
                    </Box>

                    {/* Installation Content */}
                    <Box sx={{ p: 2 }}>
                        {selectedPlatform === 'android' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    1. Add the following repository to your project level <code>build.gradle</code> file. GitHub packages require credentials for access. While our repository is public and can be accessed with any GitHub user, you'll need to create a <a href="https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens" target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'underline' }}>Personal Access Token (PAT)</a> to use as GITHUB_PACKAGE_TOKEN:
                                </Typography>
                                <CopyBlock
                                    text={`// Project level build.gradle
allprojects {
    repositories {
        // Standard repositories
        google()
        mavenCentral()
        
        // Finvu SDK repository
        maven { 
            url 'https://maven.pkg.github.com/Cookiejar-technologies/finvu_android_sdk' 
            credentials {
                username = System.getenv("GITHUB_PACKAGE_USERNAME")
                password = System.getenv("GITHUB_PACKAGE_TOKEN")
            }
        }
    }
}`}
                                    language="groovy"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Add the following in app level <code>build.gradle</code> file:
                                </Typography>
                                <CopyBlock
                                    text={`// App level build.gradle
android {
    defaultConfig {
        // Minimum SDK version required by Finvu SDK
        minSdkVersion 25
        targetSdkVersion 35
    }
}`}
                                    language="groovy"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. Add the following dependencies in your app level <code>build.gradle</code> file:
                                </Typography>
                                <CopyBlock
                                    text={`// App level build.gradle
dependencies {
    // Finvu SDK dependencies
    implementation("com.finvu.android:client-sdk:1.1.0")
    implementation("com.finvu.android:core-sdk:1.0.9")
}`}
                                    language="groovy"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'ios' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    Add the following to your <code>Podfile</code>:
                                </Typography>
                                <CopyBlock
                                    text={`# Podfile configuration
platform :ios, '16.0'
    
# Add Finvu SDK dependency
# Check the latest sdk version in the Latest SDK Versions section
pod 'FinvuSDK', :git => 'https://github.com/Cookiejar-technologies/finvu_ios_sdk.git', :tag => '1.1.1'`}
                                    language="ruby"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'flutter' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    1. Add dependencies in your <code>pubspec.yaml</code>:
                                </Typography>
                                <CopyBlock
                                    text={`dependencies:
  flutter:
    sdk: flutter
  #Check the latest sdk version in the Latest SDK Versions section                                       
  finvu_flutter_sdk:
    git:
      url: https://github.com/Cookiejar-technologies/finvu_flutter_sdk.git
      path: client
      ref: 1.0.8
  #Check the latest sdk version in the Latest SDK Versions section                                       
  finvu_flutter_sdk_core:
    git:
      url: https://github.com/Cookiejar-technologies/finvu_flutter_sdk.git
      path: core
      ref: 1.0.8`}
                                    language="yaml"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2.  Add the following repository to your project level build.gradle file. GitHub packages require credentials for access.
                                    While our repository is public and can be accessed with any GitHub user,
                                    you'll need to create a <a href="https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens" target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'underline' }}>Personal Access Token (PAT)</a> to use as GITHUB_PACKAGE_TOKEN:
                                </Typography>
                                <CopyBlock
                                    text={`allprojects {
    repositories {
        google()
        mavenCentral()
        
        // Add these lines
        maven { 
            url 'https://maven.pkg.github.com/Cookiejar-technologies/finvu_android_sdk' 
            credentials {
                username = System.getenv("GITHUB_PACKAGE_USERNAME")
                password = System.getenv("GITHUB_PACKAGE_TOKEN")
            }
        }
    }
}`}
                                    language="groovy"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. On Android, add the following to your app level <code>build.gradle</code> file:
                                </Typography>
                                <CopyBlock
                                    text={`defaultConfig {
    minSdkVersion 25
    targetSdkVersion 35
}`}
                                    language="groovy"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    4. On iOS, add the following to your <code>Podfile</code>:
                                </Typography>
                                <CopyBlock
                                    text={`target 'Runner' do
  use_frameworks!
  use_modular_headers!

  ## Add these lines
  platform :ios, '16.0'
  pod 'FinvuSDK' , :git => 'https://github.com/Cookiejar-technologies/finvu_ios_sdk.git', :tag => '1.1.1'

  flutter_install_all_ios_pods File.dirname(File.realpath(__FILE__))
end

post_install do |installer|
  installer.pods_project.targets.each do |target|
    flutter_additional_ios_build_settings(target)
    
    // Add these lines
    target.build_configurations.each do |config|
      config.build_settings['IPHONEOS_DEPLOYMENT_TARGET'] = '16.0'
    end
  end
end`}
                                    language="ruby"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'react-native' && <ReactNativeSDKInstallation />}
                    </Box>
                </Paper>
            </Box>

            {/* ProGuard Rules Section */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                ProGuard Rules (Android Only)
            </Typography>

            <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                For Android release builds (including Android with flutter and react native), add the following ProGuard rules to preserve Finvu SDK classes during code minification and obfuscation.
            </Typography>

            <Box sx={{ mt: 2 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    <Box sx={{ p: 2 }}>
                        <Typography sx={{ mb: 2, color: '#555' }}>
                            Add these lines to your <code>proguard-rules.pro</code> file:
                        </Typography>
                        <CopyBlock
                            text={`# Finvu SDK ProGuard Rules
-keep class com.finvu.android.publicInterface.** { <fields>; }
-keep class com.finvu.android.models.** { <fields>; }
-keep class com.finvu.android.types.** { <fields>; }`}
                            language="proguard"
                            showLineNumbers={true}
                            theme={nord}
                        />
                        <Typography sx={{ mt: 2, color: '#666', fontSize: '0.9rem', fontStyle: 'italic' }}>
                            These rules ensure that the SDK's essential classes and their fields are preserved during the build process.
                        </Typography>
                    </Box>
                </Paper>
            </Box>

            {/* SDK access , initialization and Connection Management */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                SDK access , initialization and Connection Management
            </Typography>

            <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                The <code>FinvuManager</code> class provides access to all SDK APIs. Initialize it in your application's entry point.
            </Typography>

            <Box sx={{ mt: 3 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    {/* Platform Selector */}
                    <Box sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.12)',
                        display: 'flex',
                        gap: 1,
                        p: 1
                    }}>
                        {['Android', 'iOS', 'Flutter', 'React-Native'].map((platform) => (
                            <Typography
                                key={platform.toLowerCase()}
                                onClick={() => handlePlatformChange(platform.toLowerCase())}
                                sx={{
                                    px: 3,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem',
                                    fontWeight: selectedPlatform === platform.toLowerCase() ? 600 : 400,
                                    color: selectedPlatform === platform.toLowerCase() ? '#0b3452' : '#666',
                                    bgcolor: selectedPlatform === platform.toLowerCase() ? 'rgba(11, 52, 82, 0.08)' : 'transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: 'rgba(11, 52, 82, 0.05)',
                                        color: '#0b3452'
                                    }
                                }}
                            >
                                {platform}
                            </Typography>
                        ))}
                    </Box>

                    {/* SDK Access & Initialization Content */}
                    <Box sx={{ p: 2 }}>
                        {selectedPlatform === 'ios' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    FinvuManager class is a singleton, and can be accessed as follows:
                                </Typography>
                                <CopyBlock
                                    text={`// Get shared instance
let finvuManager = FinvuManager.shared`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    1. Initialize with config:
                                </Typography>
                                <Typography sx={{ color: '#555' }}>
                                    Initialize with config Initialize API allows you to configure the finvuEndpoint. This is a way to configure the SDK to point to specific environments.
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    urls: UAT: "wss://webvwdev.finvu.in/consentapiv2", PROD: "wss://wsslive.finvu.in/consentapiv2"
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    <strong>Note:</strong> <code>FinvuConfig</code> is a protocol (interface). You can implement it using <code>FinvuClientConfig</code> class. The <code>finvuSnaAuthConfig</code> parameter should be set to <code>nil</code> for now.
                                </Typography>

                                <Typography sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                                    Define FinvuClientConfig:
                                </Typography>
                                <CopyBlock
                                    text={`final class FinvuClientConfig: FinvuConfig {
    var finvuSnaAuthConfig: FinvuSnaAuthConfig?
    var finvuEndpoint: URL
    var certificatePins: [String]?
    
    init(finvuEndpoint: URL, certificatePins: [String]?, finvuSnaAuthConfig: FinvuSnaAuthConfig?) {
        self.finvuEndpoint = finvuEndpoint
        self.certificatePins = certificatePins
        self.finvuSnaAuthConfig = finvuSnaAuthConfig
    }
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                                    Usage example:
                                </Typography>
                                <CopyBlock
                                    text={`let finvuUrl = URL(string: "wss://wsslive.finvu.in/consentapiv2")!
let finvuClientConfig = FinvuClientConfig(
    finvuEndpoint: finvuUrl,
    certificatePins: [
        "TmZriS3UEzT3t5s8SJATgFdUH/llYL8vieP2wOuBAB8=", // Pin from certificate #1
        "aBcDeFgHiJkLmNoPqRsTuVwXyZ1234567890+/="       // Pin from certificate #2
    ],
    finvuSnaAuthConfig: nil
)

// Initialize SDK
finvuManager.initializeWith(config: finvuClientConfig)`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                                {sslCertificatePinningInfoComponent}
                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Connect:
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Connect Finvu exposes websocket APIs that the sdk interacts with. Before making any other calls, connect method should be called. This is an async method, so await should be used to ensure its completion.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.connect { error in
    DispatchQueue.main.async {
        if let error = error {
            let errorCode = error.errorCode ?? ""
            let errorMessage = error.errorMessage ?? ""
            let localized = error.localizedDescription
            print("FinvuManager.connect - Error Code: \(errorCode), " +
                  "Error Message: \(errorMessage), " +
                  "Localized: \(localized.isEmpty ? "" : localized)")
            // Optional: Update UI state
            // self.errorMessage = "Connection failed: \(localized.isEmpty ? "Connection failed" : localized)"
            // self.showError = true
            return
        }
        print("Connected successfully")
    }
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. Disconnect:
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Disconnects from the Finvu AA server.
                                </Typography>
                                <CopyBlock
                                    text={`finvuManager.disconnect()`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    4. Check Connection Status:
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Checks if SDK is currently connected to Finvu AA server.
                                </Typography>
                                <CopyBlock
                                    text={`finvuManager.isConnected()`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'android' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    FinvuManager class is a singleton, and can be accessed as follows:
                                </Typography>
                                <CopyBlock
                                    text={`// Get singleton instance
val finvuManager = FinvuManager.shared`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    1. Initialize with config:
                                </Typography>
                                <Typography sx={{ color: '#555' }}>
                                    Initialize the SDK in your application's entry point (eg. Application class).
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    urls: UAT: "wss://webvwdev.finvu.in/consentapiv2", PROD: "wss://wsslive.finvu.in/consentapiv2"
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    <strong>Note:</strong> <code>FinvuConfig</code> is an interface. You can implement it directly using object expression. The <code>finvuSnaAuthConfig</code> parameter should be set to <code>null</code> for now.
                                </Typography>
                                
                                <CopyBlock
                                    text={`val finvuConfig = object : FinvuConfig {
    override val finvuEndpoint: String = "wss://wsslive.finvu.in/consentapiv2"
    override val certificatePins: List<String>? = listOf(
        "TmZriS3UEzT3t5s8SJATgFdUH/llYL8vieP2wOuBAB8=", // Pin from certificate #1
        "aBcDeFgHiJkLmNoPqRsTuVwXyZ1234567890+/="       // Pin from certificate #2
    )
    override val finvuSNAAuthConfig: FinvuSNAAuthConfig? = null
}

// Initialize SDK
finvuManager.initialize(finvuConfig)`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                                {sslCertificatePinningInfoComponent}
                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Connect:
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Finvu exposes websocket APIs that the sdk interacts with. Before making any other calls, connect method should be called.
                                </Typography>
                                <CopyBlock
                                    text={`finvuManager.connect { result ->
    runOnUiThread {
        if (result.isSuccess) {
            Toast.makeText(this, "Connected", Toast.LENGTH_SHORT).show()
        } else {
            when ((result.exceptionOrNull() as FinvuException).code) {
                FinvuErrorCode.SSL_PINNING_FAILURE_ERROR.code -> {
                    Toast.makeText(this, "Your connection is insecure", Toast.LENGTH_SHORT)
                        .show()
                }
                else -> {
                    Toast.makeText(
                        this,
                        "Something went wrong \${(result.exceptionOrNull() as FinvuException).code}",
                        Toast.LENGTH_SHORT
                    ).show()
                }
            }
        }
    }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. Disconnect:
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Disconnects from the Finvu AA server.
                                </Typography>
                                <CopyBlock
                                    text={`finvuManager.disconnect()`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    4. Check Connection Status:
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Checks if SDK is currently connected to Finvu AA server.
                                </Typography>
                                <CopyBlock
                                    text={`finvuManager.isConnected()`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'flutter' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    FinvuManager class is a singleton, and can be accessed as follows:
                                </Typography>
                                <CopyBlock
                                    text={`// Get FinvuManager instance
final FinvuManager finvuManager = FinvuManager();`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    1. Initialize with config:
                                </Typography>
                                <Typography sx={{ color: '#555' }}>
                                    Initialize the SDK in your application's entry point (eg. splash screen).
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    urls: UAT: "wss://webvwdev.finvu.in/consentapiv2", PROD: "wss://wsslive.finvu.in/consentapiv2".
                                </Typography>
                                <CopyBlock
                                    text={`finvuManager.initialize(FinvuConfig(
    finvuEndpoint: "wss://wsslive.finvu.in/consentapiv2",
     certificatePins: [
        "TmZriS3UEzT3t5s8SJATgFdUH/llYL8vieP2wOuBAB8=", // Pin from certificate #1
        "aBcDeFgHiJkLmNoPqRsTuVwXyZ1234567890+/="       // Pin from certificate #2
    ]
));`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                                {sslCertificatePinningInfoComponent}
                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Connect:
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Finvu exposes websocket APIs that the sdk interacts with. Before making any other calls, connect method should be called. This is an async method, so await should be used to ensure its completion.
                                </Typography>
                                <CopyBlock
                                    text={`try {
    await finvuManager.connect();
    print('Connected successfully');
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.connect - Error Code: $errorCode, Error Message: $errorMessage');
    
    // Handle specific error codes
    if (errorCode == '8000' || errorCode == 'F402') {
        // Session disconnected - retry connection
        await Future.delayed(Duration(seconds: 2));
        await finvuManager.connect();
    } else if (errorCode == '8001') {
        // SSL pinning failure
        showError('SSL certificate validation failed. Please check your certificate pins.');
    } else {
        showError('Connection failed: \${errorMessage.isEmpty ? 'Unable to connect' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. Disconnect:
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Disconnects from the Finvu AA server.
                                </Typography>
                                <CopyBlock
                                    text={`await finvuManager.disconnect();`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    4. Check Connection Status:
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Checks if SDK is currently connected to Finvu AA server.
                                </Typography>
                                <CopyBlock
                                    text={`try {
    final isConnected = await finvuManager.isConnected();
    print('Connection status: $isConnected');
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.isConnected - Error Code: $errorCode, Error Message: $errorMessage');
    // If connection check fails, assume disconnected
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'react-native' && <ReactNativeSDKInitialisationAndConnectionManagement sslCertificatePinningInfoComponent={sslCertificatePinningInfoComponent} />}
                    </Box>
                </Paper>
            </Box>

            {/* Authentication Section */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Authentication
            </Typography>

            <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                Authentication involves two steps: initiating login with a consent handle and verifying the OTP received.
            </Typography>

            <Box sx={{ mt: 2 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    {/* Platform Selector */}
                    <Box sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.12)',
                        display: 'flex',
                        gap: 1,
                        p: 1
                    }}>
                        {['Android', 'iOS', 'Flutter', 'React-Native'].map((platform) => (
                            <Typography
                                key={platform.toLowerCase()}
                                onClick={() => handlePlatformChange(platform.toLowerCase())}
                                sx={{
                                    px: 3,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem',
                                    fontWeight: selectedPlatform === platform.toLowerCase() ? 600 : 400,
                                    color: selectedPlatform === platform.toLowerCase() ? '#0b3452' : '#666',
                                    bgcolor: selectedPlatform === platform.toLowerCase() ? 'rgba(11, 52, 82, 0.08)' : 'transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: 'rgba(11, 52, 82, 0.05)',
                                        color: '#0b3452'
                                    }
                                }}
                            >
                                {platform}
                            </Typography>
                        ))}
                    </Box>

                    {/* Authentication Content */}
                    <Box sx={{ p: 2 }}>
                        {selectedPlatform === 'android' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    Login with Consent Handle Initiates login process using either username or mobile number.
                                    Triggers OTP to user's registered mobile.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.loginWithUsernameOrMobileNumber(
    consentHandleId = "CONSENT_HANDLE_ID", // handle id generated for the same user handle and mobile number
    username = "USER_HANDLE", // MOBILE_NUMBER@finvu
    mobileNumber = "MOBILE_NUMBER",
) { result ->
    runOnUiThread {
        if (result.isSuccess) {
            val otpReference = result.getOrNull()
            // Process OTP reference
        } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ LoginActivity - Login with username or mobile number failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
        }
    }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    Verify Login OTP Verifies login OTP and establishes authenticated session.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.verifyLoginOtp(
    otp = "123456", // OTP received
    otpReference = otpReference // Reference from login response
) { result ->
    runOnUiThread {
        if (result.isSuccess) {
            val handleInfo = result.getOrNull()
            // Process handle info
        } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ LoginActivity - Verify login OTP failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
        }
    }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    Note: To resend OTP during login, simply call the loginWithUsernameOrMobileNumberAndConsentHandle API again with the same parameters.
                                </Typography>
                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    logout Logs out user and invalidates current session.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.logout { result ->
    runOnUiThread {
        if (result.isSuccess) {
            // Logout successful
        } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Logout failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
        }
    }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'ios' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    Login with Consent Handle Initiates login process using either username or mobile number.
                                    Triggers OTP to user's registered mobile.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.loginWith(
    username : "USER_HANDLE", // MOBILE_NUMBER@finvu
    mobileNumber : "MOBILE_NUMBER",
    consentHandleId: "CONSENT_HANDLE_ID", // handle id generated for the same user handle and mobile number
) { response, error in   
    if let error = error {
        let errorCode = error.errorCode ?? ""
        let errorMessage = error.errorMessage ?? ""
        let localized = error.localizedDescription
        print("FinvuManager.loginWith - Error Code: \(errorCode), " +
              "Error Message: \(errorMessage), " +
              "Localized: \(localized.isEmpty ? "" : localized)")
        return
    }
    // Store otpReference for verification
    let otpReference = response?.otpReference
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    Verify Login OTP Verifies login OTP and establishes authenticated session.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.verifyLoginOtp(
    otp: "123456", // OTP received
    otpReference // Reference from login response
) { handleInfo, error in
    if let error = error {
        let errorCode = error.errorCode ?? ""
        let errorMessage = error.errorMessage ?? ""
        let localized = error.localizedDescription
        print("FinvuManager.verifyLoginOtp - Error Code: \(errorCode), " +
              "Error Message: \(errorMessage), " +
              "Localized: \(localized.isEmpty ? "" : localized)")
        return
    }
    // Process handleInfo
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    Note: To resend OTP during login, simply call the loginWith API again with the same parameters.
                                </Typography>
                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    logout Logs out user and invalidates current session.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.logout { error in
    if let error = error {
        let errorCode = error.errorCode ?? ""
        let errorMessage = error.errorMessage ?? ""
        let localized = error.localizedDescription
        print("FinvuManager.logout - Error Code: \(errorCode), " +
              "Error Message: \(errorMessage), " +
              "Localized: \(localized.isEmpty ? "" : localized)")
        return
    }
    // Logout successful
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'flutter' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    Login with Consent Handle Initiates login process using either username or mobile number. Triggers OTP to user's registered mobile.
                                </Typography>
                                <CopyBlock
                                    text={`try {
    // Initial login or Resend OTP
    final otpReference = await finvuManager.loginWithUsernameOrMobileNumberAndConsentHandle(
        username: "USER_HANDLE", // MOBILE_NUMBER@finvu
        mobileNumber: "MOBILE_NUMBER",
        consentHandleId: "CONSENT_HANDLE_ID", // handle id generated for the same user handle and mobile number
    );
    // Store otpReference for verification
    print('OTP Reference: \${otpReference.reference}');
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.loginWithUsernameOrMobileNumberAndConsentHandle - Error Code: $errorCode, Error Message: $errorMessage');
    
    // Handle specific error codes
    if (errorCode == 'A001') {
        showError('Invalid login format. Please check your username or mobile number.');
    } else if (errorCode == 'A003') {
        showError('Failed to send OTP. Please try again.');
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect and retry
        await finvuManager.disconnect();
        await finvuManager.connect();
        // Retry login
    } else {
        showError('Login failed: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    Verify Login OTP Verifies login OTP and establishes authenticated session.
                                </Typography>
                                <CopyBlock
                                    text={`try {
    final handleInfo = await finvuManager.verifyLoginOtp(
        otp: "123456", // OTP received
        otpReference: otpReference.reference, // Reference from login response
    );
    // Process handleInfo
    print('Login successful. User ID: \${handleInfo.userId}');
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.verifyLoginOtp - Error Code: $errorCode, Error Message: $errorMessage');
    
    // Handle specific error codes
    if (errorCode == 'A005') {
        showError('Invalid OTP. Please check and try again.');
        // Allow user to retry or resend OTP
    } else if (errorCode == 'A006') {
        showError('Invalid OTP reference. Session expired. Please login again.');
        // Restart login flow
    } else if (errorCode == 'A004') {
        showError('OTP verification failed. Please try again.');
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect
        await finvuManager.disconnect();
        await finvuManager.connect();
        // Restart login flow
    } else {
        showError('OTP verification failed: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    Note: To resend OTP during login, simply call the loginWithUsernameOrMobileNumberAndConsentHandle API again with the same parameters.
                                </Typography>
                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    logout Logs out user and invalidates current session.
                                </Typography>
                                <CopyBlock
                                    text={`try {
    await finvuManager.logout();
    print('Logout successful');
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.logout - Error Code: $errorCode, Error Message: $errorMessage');
    
    // Even if logout fails, disconnect to clean up
    finvuManager.disconnect();
    
    if (errorCode == 'F402' || errorCode == 'A002') {
        // Session already invalid - just disconnect
        print('Session already invalidated');
    } else {
        showError('Logout failed: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'react-native' && <ReactNativeSDKAuthentication />}
                    </Box>
                </Paper>
            </Box>

            {/* FIP Management Section */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                FIP Management
            </Typography>

            <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                FIP Management allows you to fetch and manage Financial Information Providers (FIPs).
            </Typography>

            <Box sx={{ mt: 2 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    <Box sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.12)',
                        display: 'flex',
                        gap: 1,
                        p: 1
                    }}>
                        {['Android', 'iOS', 'Flutter', 'React-Native'].map((platform) => (
                            <Typography
                                key={platform.toLowerCase()}
                                onClick={() => handlePlatformChange(platform.toLowerCase())}
                                sx={{
                                    px: 3,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem',
                                    fontWeight: selectedPlatform === platform.toLowerCase() ? 600 : 400,
                                    color: selectedPlatform === platform.toLowerCase() ? '#0b3452' : '#666',
                                    bgcolor: selectedPlatform === platform.toLowerCase() ? 'rgba(11, 52, 82, 0.08)' : 'transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: 'rgba(11, 52, 82, 0.05)',
                                        color: '#0b3452'
                                    }
                                }}
                            >
                                {platform}
                            </Typography>
                        ))}
                    </Box>

                    <Box sx={{ p: 2 }}>
                        {selectedPlatform === 'flutter' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    1. Fetch All FIP Options
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Retrieves list of all available Financial Information Providers (FIPs) and contains the FiTypes required by the FIP.
                                </Typography>
                                <CopyBlock
                                    text={`try {
    final fipList = await finvuManager.fipsAllFIPOptions();
    print('Fetched \${fipList.length} FIP options');
    // Process FIP list
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.fipsAllFIPOptions - Error Code: $errorCode, Error Message: $errorMessage');
    
    if (errorCode == 'F404') {
        showError('No FIPs available at the moment.');
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect
        await finvuManager.disconnect();
        await finvuManager.connect();
    } else {
        showError('Failed to fetch FIPs: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Fetch FIP Details
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Fetches detailed information about specific FIP ID received from the fipsAllFIPOptions API.
                                    Also contains identifiers required by the FIP for discovery.
                                    The returned <code>FinvuFIPDetails</code> includes an optional <code>linkingOtpLength</code> (<code>int?</code>) — when present, it indicates the length of the FIP linking OTP used for linking accounts (e.g. for OTP input UI).
                                </Typography>
                                <CopyBlock
                                    text={`try {
    final fipDetails = await finvuManager.fetchFIPDetails("FIP_ID");
    print('FIP Details: \${fipDetails.fipId}');
    final linkingOtpLength = fipDetails.linkingOtpLength;
    // Process FIP details
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.fetchFIPDetails - Error Code: $errorCode, Error Message: $errorMessage');
    
    if (errorCode == 'F406' || errorCode == 'D007') {
        showError('Invalid FIP. Please select a valid FIP.');
    } else if (errorCode == 'F404') {
        showError('FIP details not found.');
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect
        await finvuManager.disconnect();
        await finvuManager.connect();
    } else {
        showError('Failed to fetch FIP details: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'android' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    1. Fetch All FIP Options
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Retrieves list of all available Financial Information Providers (FIPs).
                                </Typography>
                                <CopyBlock
                                    text={`finvuManager.fipsAllFIPOptions { result ->
    runOnUiThread {
        if (result.isSuccess) {
            val fipList = result.getOrNull()
            // Process FIP list
        } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Fips all FIP options failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
        }
    }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Fetch FIP Details
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Fetches detailed information about specific FIP. The returned <code>FipDetails</code> includes an optional <code>linkingOtpLength</code> (<code>Int?</code>) — when present, it indicates the length of the FIP linking OTP used for linking accounts (e.g. for OTP input UI).
                                </Typography>
                                <CopyBlock
                                    text={`finvuManager.fetchFIPDetails(fipId = "FIP_ID") { result ->
    runOnUiThread {
        if (result.isSuccess) {
            val fipDetails = result.getOrNull()
            val linkingOtpLength = fipDetails?.linkingOtpLength
        // Process FIP details
    } else { 
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Fetch FIP details failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
        }
    }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'ios' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    1. Fetch All FIP Options
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Retrieves list of all available Financial Information Providers (FIPs).
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.fipsAllFIPOptions { response, error in
    if let error = error {
        let errorCode = error.errorCode ?? ""
        let errorMessage = error.errorMessage ?? ""
        let localized = error.localizedDescription
        print("FinvuManager.fipsAllFIPOptions - Error Code: \(errorCode), " +
              "Error Message: \(errorMessage), " +
              "Localized: \(localized.isEmpty ? "" : localized)")
        return
    }
    // Process FIP list
    let results = response?.searchOptions ?? []
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Fetch FIP Details
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Fetches detailed information about specific FIP. The returned <code>FIPDetails</code> includes an optional <code>linkingOtpLength</code> (<code>NSNumber?</code>) — when present, it indicates the length of the FIP linking OTP used for linking accounts (e.g. for OTP input UI).
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.fetchFIPDetails(fipId: "FIP_ID") { details, error in
    if let error = error {
        let errorCode = error.errorCode ?? ""
        let errorMessage = error.errorMessage ?? ""
        let localized = error.localizedDescription
        print("FinvuManager.fetchFIPDetails - Error Code: \(errorCode), " +
              "Error Message: \(errorMessage), " +
              "Localized: \(localized.isEmpty ? "" : localized)")
        return
    }
    let linkingOtpLength = details?.linkingOtpLength
    // Process FIP details
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'react-native' && <ReactNativeSDKFIPManagement />}
                    </Box>
                </Paper>

            </Box>

            {/* Account Discovery Section */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Account Discovery
            </Typography>

            <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                Account discovery is a two-step process: first fetching available FIP options, then initiating the discovery flow with the selected FIP.
            </Typography>



            <Box sx={{ mt: 2 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    {/* Platform Selector */}
                    <Box sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.12)',
                        display: 'flex',
                        gap: 1,
                        p: 1
                    }}>
                        {['Android', 'iOS', 'Flutter', 'React-Native'].map((platform) => (
                            <Typography
                                key={platform.toLowerCase()}
                                onClick={() => handlePlatformChange(platform.toLowerCase())}
                                sx={{
                                    px: 3,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem',
                                    fontWeight: selectedPlatform === platform.toLowerCase() ? 600 : 400,
                                    color: selectedPlatform === platform.toLowerCase() ? '#0b3452' : '#666',
                                    bgcolor: selectedPlatform === platform.toLowerCase() ? 'rgba(11, 52, 82, 0.08)' : 'transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: 'rgba(11, 52, 82, 0.05)',
                                        color: '#0b3452'
                                    }
                                }}
                            >
                                {platform}
                            </Typography>
                        ))}
                    </Box>

                    {/* Account Discovery Content */}
                    <Box sx={{ p: 2 }}>
                        {selectedPlatform === 'android' && (
                            <>
                                <Typography variant="h6" sx={{ color: "#0b3452", fontWeight: "bold" }}>
                                    Account Discovery Methods
                                </Typography>

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    1. Discover Accounts
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Discovers user accounts with specified FIP using given identifiers.
                                </Typography>
                                <CopyBlock
                                    text={`// Method signature
FinvuManager.shared.discoverAccounts(
    fipId: String,
    fiTypes: List<String>,
    finvuIdentifiers: List<FinvuIdentifier>
) { result ->
    runOnUiThread {
        if (result.isSuccess) {
            val discoveredAccounts = result.getOrNull()?.discoveredAccounts
        // Process discovered accounts
        } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Discover accounts failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
        }
    }
}

// Usage example
FinvuManager.shared.discoverAccounts(
    fipId: fipId,
    fiTypes: ["DEPOSIT", "RECURRING_DEPOSIT"],
    identifiers: [
        TypeIdentifierInfo(
                    "WEAK",
                    "PAN",
                    panText
        )
    ]
) { result ->
    runOnUiThread {
    if (result.isSuccess) {
        val discoveredAccounts = result.getOrNull()?.discoveredAccounts
        // Process discovered accounts
    } else { 
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Discover accounts failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
        }
    }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Discover Accounts Async (Asynchronous)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Executes an asynchronous request. Preferred when discovery process might take longer.
                                </Typography>
                                <CopyBlock
                                    text={`// Method signature
FinvuManager.shared.discoverAccountsAsync(
    fipId: String,
    fiTypes: List<String>,
    finvuIdentifiers: List<FinvuIdentifier>
) { result ->
    runOnUiThread {
        if (result.isSuccess) {
            val discoveredAccounts = result.getOrNull()?.discoveredAccounts
        // Process discovered accounts
        } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Discover accounts async failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
        }
    }
}

// Usage example
FinvuManager.shared.discoverAccountsAsync(
    fipId: fipId,
    fiTypes: ["DEPOSIT", "RECURRING_DEPOSIT"],
    identifiers: [
        TypeIdentifierInfo(
                    "WEAK",
                    "PAN",
                    panText
        )
    ]
) { result ->
    runOnUiThread {
    if (result.isSuccess) {
        val discoveredAccounts = result.getOrNull()?.discoveredAccounts
        // Process discovered accounts
    } else { 
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Discover accounts async failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
        }
    }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                                    Identifier Examples
                                </Typography>

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    1. Banks require Mobile Number as a strong type.
                                </Typography>
                                <CopyBlock
                                    text={`listOf(
    TypeIdentifierInfo(
                    "WEAK",
                    "PAN",
                    panText
    )
)`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Investments require same as bank the mobile as first identifier and additional weak PAN as a second identifier.
                                </Typography>
                                <CopyBlock
                                    text={`listOf(
    TypeIdentifierInfo(
        category = "STRONG",
        type = "MOBILE",
        value = "930910XXXX"
    ),
    TypeIdentifierInfo(
        category = "WEAK",
        type = "PAN",
        value = "DFKPGXXXXR"
    )
)`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. Insurance require same as bank the mobile as first identifier and additional needs DOB as a second identifier.
                                </Typography>
                                <CopyBlock
                                    text={`listOf(
    TypeIdentifierInfo(
        category = "STRONG",
        type = "MOBILE",
        value = "930910XXXX"
    ),
    TypeIdentifierInfo(
        category = "ANCILLARY",
        type = "DOB",
        value = "yyyy-MM-dd"
    )
)`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'ios' && (
                            <>
                                <Typography variant="h6" sx={{ color: "#0b3452", fontWeight: "bold" }}>
                                    Account Discovery Methods
                                </Typography>

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    1. Discover Accounts
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Discovers user accounts with specified FIP using given identifiers.
                                </Typography>
                                <CopyBlock
                                    text={`// Method signature
FinvuManager.shared.discoverAccounts(
    fipId: String,
    fiTypes: [String],
    identifiers: [FinvuIdentifier],
    completion: @escaping (Result<[FinvuDiscoveredAccountInfo], Error>) -> Void
)

// Usage example
FinvuManager.shared.discoverAccounts(
    fipId: fipId,
    fiTypes: ["DEPOSIT", "RECURRING_DEPOSIT"],
    identifiers: [
        FinvuIdentifier(
            category: "STRONG",
            type: "MOBILE",
            value: "9309107496"
        )
    ]
) { result in
    switch result {
    case .success(let accounts):
        // Process discovered accounts
        break
    case .failure(let error):
        if let nsError = error as NSError? {
            let errorCode = nsError.userInfo["errorCode"] as? String ?? "\(nsError.code)"
            let errorMessage = nsError.userInfo["errorMessage"] as? String ?? ""
            let localized = error.localizedDescription
            print("FinvuManager.discoverAccounts - Error Code: \(errorCode), " +
                  "Error Message: \(errorMessage), " +
                  "Localized: \(localized.isEmpty ? "" : localized)")
        }
    }
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Discover Accounts Async (Asynchronous)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Executes an asynchronous request. Preferred when discovery process might take longer.
                                </Typography>
                                <CopyBlock
                                    text={`// Method signature
FinvuManager.shared.discoverAccountsAsync(
    fipId: String,
    fiTypes: [String],
    identifiers: [FinvuIdentifier],
    completion: @escaping (Result<[FinvuDiscoveredAccountInfo], Error>) -> Void
)

// Usage example
FinvuManager.shared.discoverAccountsAsync(
    fipId: fipId,
    fiTypes: ["DEPOSIT", "RECURRING_DEPOSIT"],
    identifiers: [
        FinvuIdentifier(
            category: "STRONG",
            type: "MOBILE",
            value: "9309107496"
        )
    ]
) { result in
    switch result {
    case .success(let accounts):
        // Process discovered accounts
        break
    case .failure(let error):
        if let nsError = error as NSError? {
            let errorCode = nsError.userInfo["errorCode"] as? String ?? "\(nsError.code)"
            let errorMessage = nsError.userInfo["errorMessage"] as? String ?? ""
            let localized = error.localizedDescription
            print("FinvuManager.discoverAccountsAsync - Error Code: \(errorCode), " +
                  "Error Message: \(errorMessage), " +
                  "Localized: \(localized.isEmpty ? "" : localized)")
        }
    }
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                                <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                                    Identifier Examples
                                </Typography>

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    1. Banks require Mobile Number as a strong type.
                                </Typography>
                                <CopyBlock
                                    text={`[
    TypeIdentifierInfo(
        category: "STRONG",
        type: "MOBILE",
        value: "930910XXXX"
    )
]`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Investments require same as bank the mobile as first identifier and additional weak PAN as a second identifier.
                                </Typography>
                                <CopyBlock
                                    text={`[
    TypeIdentifierInfo(
        category: "STRONG",
        type: "MOBILE",
        value: "930910XXXX"
    ),
    TypeIdentifierInfo(
        category: "WEAK",
        type: "PAN",
        value: "DFKPGXXXXR"
    )
]`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. Insurance require same as bank the mobile as first identifier and additional needs DOB as a second identifier.
                                </Typography>
                                <CopyBlock
                                    text={`[
    TypeIdentifierInfo(
        category: "STRONG",
        type: "MOBILE",
        value: "930910XXXX"
    ),
    TypeIdentifierInfo(
        category: "ANCILLARY",
        type: "DOB",
        value: "yyyy-MM-dd"
    )
]`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'flutter' && (
                            <>
                                <Typography variant="h6" sx={{ color: "#0b3452", fontWeight: "bold" }}>
                                    Account Discovery Methods
                                </Typography>

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    1. Discover Accounts (Synchronous)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Executes a synchronous request to discover accounts. Use when you need immediate results.
                                </Typography>
                                <CopyBlock
                                    text={`// Method signature
Future<List<FinvuDiscoveredAccountInfo>> discoverAccounts(
    String fipId,
    List<String> fiTypes,
    List<FinvuTypeIdentifierInfo> identifiers)

// Usage example
try {
    final discoveredAccounts = await finvuManager.discoverAccounts(
        fipId,
        ["DEPOSIT", "RECURRING_DEPOSIT"],
        [
            FinvuTypeIdentifierInfo(
                category: "STRONG",
                type: "MOBILE",
                value: "9309107496"
            )
        ]
    );
    print('Discovered \${discoveredAccounts.length} accounts');
    // Process discovered accounts
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.discoverAccounts - Error Code: $errorCode, Error Message: $errorMessage');
    
    // Handle specific error codes
    if (errorCode == 'D001') {
        showError('User ID mismatch. Please login again.');
    } else if (errorCode == 'D002') {
        showError('Unsupported financial information type.');
    } else if (errorCode == 'D003') {
        showError('Device not authenticated. Please complete device authentication.');
    } else if (errorCode == 'D004') {
        showError('Email not authenticated. Please verify your email.');
    } else if (errorCode == 'D005') {
        showError('Aadhar not authenticated. Please complete Aadhar authentication.');
    } else if (errorCode == 'D006') {
        showError('Authenticator required for this operation.');
    } else if (errorCode == 'D007' || errorCode == 'F406') {
        showError('Invalid FIP. Please select a valid FIP.');
    } else if (errorCode == 'D008') {
        showError('FIP operation failed. Please try again later.');
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect
        await finvuManager.disconnect();
        await finvuManager.connect();
    } else {
        showError('Account discovery failed: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Discover Accounts Async (Asynchronous)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Executes an asynchronous request. Preferred when discovery process might take longer.
                                </Typography>
                                <CopyBlock
                                    text={`// Method signature
Future<List<FinvuDiscoveredAccountInfo>> discoverAccountsAsync(
    String fipId,
    List<String> fiTypes,
    List<FinvuTypeIdentifierInfo> identifiers)

// Usage example
try {
    final discoveredAccounts = await finvuManager.discoverAccountsAsync(
        fipId,
        ["DEPOSIT", "RECURRING_DEPOSIT"],
        [
            FinvuTypeIdentifierInfo(
                category: "STRONG",
                type: "MOBILE",
                value: "9309107496"
            )
        ]
    );
    print('Discovered \${discoveredAccounts.length} accounts');
    // Process discovered accounts
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.discoverAccountsAsync - Error Code: $errorCode, Error Message: $errorMessage');
    
    // Handle specific error codes (same as discoverAccounts)
    if (errorCode == 'D008') {
        // FIP operation failed - may take time
        showError('Account discovery is in progress. This may take some time. Please wait.');
        // Show loading indicator and retry later or check status
    } else if (errorCode == 'D001') {
        showError('User ID mismatch. Please login again.');
    } else if (errorCode == 'D002') {
        showError('Unsupported financial information type.');
    } else if (errorCode == 'D003') {
        showError('Device not authenticated. Please complete device authentication.');
    } else if (errorCode == 'D007' || errorCode == 'F406') {
        showError('Invalid FIP. Please select a valid FIP.');
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect
        await finvuManager.disconnect();
        await finvuManager.connect();
    } else {
        showError('Account discovery failed: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                                    Identifier Examples
                                </Typography>

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    1. Banks require Mobile Number as a strong type
                                </Typography>
                                <CopyBlock
                                    text={`[
    FinvuTypeIdentifierInfo(
        category: "STRONG",
        type: "MOBILE",
        value: "930910XXXX"
    )
]`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Investments require same as bank the mobile as first identifier and additional weak PAN as a second identifier
                                </Typography>
                                <CopyBlock
                                    text={`[
    FinvuTypeIdentifierInfo(
        category: "STRONG",
        type: "MOBILE",
        value: "930910XXXX"
    ),
    FinvuTypeIdentifierInfo(
        category: "WEAK",
        type: "PAN",
        value: "DFKPGXXXXR"
    )
]`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. Insurance require same as bank the mobile as first identifier and additional needs DOB as a second identifier.
                                </Typography>
                                <CopyBlock
                                    text={`[
    FinvuTypeIdentifierInfo(
        category: "STRONG",
        type: "MOBILE",
        value: "930910XXXX"
    ),
    FinvuTypeIdentifierInfo(
        category: "ANCILLARY",
        type: "DOB",
        value: "yyyy-MM-dd"
    )
]`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'react-native' && <ReactNativeSDKAccountDiscovery />}
                        {selectedPlatform !== 'React-Native' && (
                            <>
                                <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                                    Difference Between discoverAccounts and discoverAccountsAsync
                                </Typography>

                                <Box sx={{ p: 2 }}>
                                    <Typography variant="subtitle1" sx={{ color: "#0b3452", fontWeight: "bold" }}>
                                        1. discoverAccounts (Synchronous)
                                    </Typography>
                                    <Typography sx={{ mt: 1, mb: 2, color: '#555' }}>
                                        Executes a synchronous request to discover accounts. The backend processes the request and returns the results immediately.
                                    </Typography>
                                    <Box sx={{ pl: 2, borderLeft: '3px solid #0b3452' }}>
                                        <Typography sx={{ color: '#555' }}>
                                            <strong>Use Case:</strong> When you need real-time results and the account discovery process is expected to complete quickly.
                                        </Typography>
                                    </Box>

                                    <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                                        2. discoverAccountsAsync (Asynchronous)
                                    </Typography>
                                    <Typography sx={{ mt: 1, mb: 2, color: '#555' }}>
                                        Executes an asynchronous request to discover accounts. The backend processes the request in the background, and the results may take longer to return.
                                    </Typography>
                                    <Box sx={{ pl: 2, borderLeft: '3px solid #0b3452' }}>
                                        <Typography sx={{ color: '#555' }}>
                                            <strong>Use Case:</strong> When the account discovery process might take more time, or when you can afford to wait for the results without blocking the user interface.
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        mt: 4,
                                        p: 2,
                                        bgcolor: 'rgba(11, 52, 82, 0.05)',
                                        borderRadius: '4px'
                                    }}>
                                        <Typography variant="subtitle1" sx={{ color: "#0b3452", fontWeight: "bold" }}>
                                            Summary
                                        </Typography>
                                        <Box component="ul" sx={{ mt: 1, pl: 3 }}>
                                            <li>
                                                <Typography sx={{ color: '#555' }}>
                                                    Use <code>discoverAccounts</code> for immediate results in scenarios where quick responses are critical.
                                                </Typography>
                                            </li>
                                            <li>
                                                <Typography sx={{ color: '#555' }}>
                                                    Use <code>discoverAccountsAsync</code> for background processing when the discovery process is expected to take longer or can be deferred.
                                                </Typography>
                                            </li>
                                        </Box>
                                    </Box>
                                </Box>
                            </>
                        )}

                        <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                            Notes:
                        </Typography>
                        <Box component="ul" sx={{ mt: 1, pl: 3 }}>
                            <li>
                                <Typography sx={{ color: '#555' }}>
                                    Common FiTypes: DEPOSIT, TERM_DEPOSIT, RECURRING_DEPOSIT, INSURANCE_POLICIES, LIFE_INSURANCE, GENERAL_INSURANCE, MUTUAL_FUNDS, EQUITIES, GSTR1_3B, NPS, SIP
                                </Typography>
                            </li>
                            <li>
                                <Typography sx={{ color: '#555' }}>
                                    FiTypes required by specific FIP are returned in fipsAllFIPOptions API
                                </Typography>
                            </li>
                            <li>
                                <Typography sx={{ color: '#555' }}>
                                    Identifiers required for discovery are returned in fetchFIPDetails API
                                </Typography>
                            </li>
                        </Box>
                    </Box>
                </Paper>
            </Box>

            {/* Account Linking Section */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Account Linking
            </Typography>

            <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                After discovering accounts, you can initiate the linking process. This is typically a two-step process: initiating the link and confirming with OTP.
                <br />
                <strong>Note:</strong> Please fetch linked accounts first using the <code>fetchLinkedAccounts</code> API to avoid attempting to link accounts that are already linked.
            </Typography>


            <Box sx={{ mt: 2 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>

                    {/* Platform Selector */}
                    <Box sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.12)',
                        display: 'flex',
                        gap: 1,
                        p: 1
                    }}>
                        {['Android', 'iOS', 'Flutter', 'React-Native'].map((platform) => (
                            <Typography
                                key={platform.toLowerCase()}
                                onClick={() => handlePlatformChange(platform.toLowerCase())}
                                sx={{
                                    px: 3,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem',
                                    fontWeight: selectedPlatform === platform.toLowerCase() ? 600 : 400,
                                    color: selectedPlatform === platform.toLowerCase() ? '#0b3452' : '#666',
                                    bgcolor: selectedPlatform === platform.toLowerCase() ? 'rgba(11, 52, 82, 0.08)' : 'transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: 'rgba(11, 52, 82, 0.05)',
                                        color: '#0b3452'
                                    }
                                }}
                            >
                                {platform}
                            </Typography>
                        ))}
                    </Box>

                    {/* Account Linking Content */}
                    <Box sx={{ p: 2 }}>
                        {selectedPlatform === 'flutter' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    1. Link Accounts
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Initiates account linking process for discovered accounts by passing a mapped FIP-Details and the user selected accounts.
                                </Typography>
                                <CopyBlock
                                    text={`// Method signature
Future<FinvuAccountLinkingRequestReference> linkAccounts(
    FinvuFIPDetails fipDetails,
    List<FinvuDiscoveredAccountInfo> accounts)

// Usage example
try {
    final linkingReference = await finvuManager.linkAccounts(
        fipDetails: selectedFipDetails,
        accounts: selectedAccounts,
    );
    print('Account linking initiated. Reference: \${linkingReference.referenceNumber}');
    // Store referenceNumber for OTP confirmation
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.linkAccounts - Error Code: $errorCode, Error Message: $errorMessage');
    
    if (errorCode == 'D009') {
        showError('One or more accounts are already linked. Please check your linked accounts.');
        // Fetch linked accounts and show to user
        final linkedAccounts = await finvuManager.fetchLinkedAccounts();
    } else if (errorCode == 'D011') {
        showError('Invalid account reference. Please select valid accounts.');
    } else if (errorCode == 'D007' || errorCode == 'F406') {
        showError('Invalid FIP. Please select a valid FIP.');
    } else if (errorCode == 'D008') {
        showError('FIP operation failed. Please try again.');
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect
        await finvuManager.disconnect();
        await finvuManager.connect();
    } else {
        showError('Account linking failed: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Confirm Account Linking
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Confirms account linking using received OTP.
                                </Typography>
                                <CopyBlock
                                    text={`// Method signature
Future<FinvuConfirmAccountLinkingInfo> confirmAccountLinking(
    FinvuAccountLinkingRequestReference requestReference,
    String otp)

// Usage example
try {
    final confirmInfo = await finvuManager.confirmAccountLinking(
        requestReference: FinvuAccountLinkingRequestReference(
            referenceNumber: accountLinkingRequestReference,
        ),
        otp: "123456", // OTP received
    );
    print('Account linking confirmed. Linked \${confirmInfo.linkedAccounts.length} accounts');
    // Process successful linking
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.confirmAccountLinking - Error Code: $errorCode, Error Message: $errorMessage');
    
    if (errorCode == 'A005') {
        showError('Invalid OTP. Please check and try again.');
        // Allow user to retry
    } else if (errorCode == 'A006') {
        showError('Invalid linking reference. Please start linking process again.');
    } else if (errorCode == 'D009') {
        showError('Account already linked.');
    } else if (errorCode == 'D011') {
        showError('Invalid account reference.');
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect
        await finvuManager.disconnect();
        await finvuManager.connect();
    } else {
        showError('Account linking confirmation failed: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2, color: '#555', fontStyle: 'italic' }}>
                                    Note: To resend OTP during account linking, call the linkAccounts API again with the same parameters.
                                </Typography>

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. Fetch Linked Accounts
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Retrieves list of all linked accounts for current user.
                                </Typography>
                                <CopyBlock
                                    text={`// Method signature
Future<List<FinvuLinkedAccountDetailsInfo>> fetchLinkedAccounts()

// Usage example
try {
    final linkedAccounts = await finvuManager.fetchLinkedAccounts();
    print('Fetched \${linkedAccounts.length} linked accounts');
    // Process linked accounts
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.fetchLinkedAccounts - Error Code: $errorCode, Error Message: $errorMessage');
    
    if (errorCode == 'D010') {
        // No linked accounts found - this is normal for new users
        print('No linked accounts found. User needs to discover and link accounts.');
        // Continue with discovery flow
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect
        await finvuManager.disconnect();
        await finvuManager.connect();
    } else {
        showError('Failed to fetch linked accounts: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'android' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    1. Link Accounts
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Initiates account linking process for discovered accounts.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.linkAccounts(
    finvuAccounts = selectedAccounts,
    finvuFipDetails = fipDetails
) { result ->
        runOnUiThread {
        if (result.isSuccess) {
            val response = result.getOrNull()
            val accountLinkingRequestReference = response?.referenceNumber
            // Process account linking reference
        } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Link accounts failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
        }
        }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Confirm Account Linking
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Confirms account linking using received OTP.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.confirmAccountLinking(
    referenceNumber = accountLinkingRequestReference,
    otp = otp
) { result ->
        runOnUiThread {
            if (result.isSuccess) {
                val list = result.getOrNull()?.linkedAccounts
                // Process successful linking
            } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Confirm account linking failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
            }
        }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. Fetch Linked Accounts
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Retrieves list of all linked accounts.
                                </Typography>
                                <CopyBlock
                                    text={`finvuManager.fetchLinkedAccounts { result ->
        runOnUiThread {
                    if (result.isSuccess) {
            val list = result.getOrNull()?.linkedAccounts
                        // Process linked accounts
                    } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Fetch linked accounts failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
                    }
        }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'ios' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    1. Link Accounts
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Initiates account linking process for discovered accounts.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.linkAccounts(
    accounts: selectedAccounts,
    fipDetails: fipDetails
) { response, error in
    if let error = error {
        let errorCode = error.errorCode ?? ""
        let errorMessage = error.errorMessage ?? ""
        let localized = error.localizedDescription
        print("FinvuManager.linkAccounts - Error Code: \(errorCode), " +
              "Error Message: \(errorMessage), " +
              "Localized: \(localized.isEmpty ? "" : localized)")
        return
    }
    let accountLinkingRequestReference = response.referenceNumber
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Confirm Account Linking
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Confirms account linking using received OTP.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.confirmAccountLinking(
    linkingReference: AccountLinkingRequestReference(
        referenceNumber: accountLinkingRequestReference
    ),
    otp: otp
) { response, error in
    if let error = error {
        let errorCode = error.errorCode ?? ""
        let errorMessage = error.errorMessage ?? ""
        let localized = error.localizedDescription
        print("FinvuManager.confirmAccountLinking - Error Code: \(errorCode), " +
              "Error Message: \(errorMessage), " +
              "Localized: \(localized.isEmpty ? "" : localized)")
        return
    }
    // Process successful linking
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. Fetch Linked Accounts
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Retrieves list of all linked accounts.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.fetchLinkedAccounts { result, error in
    if let error = error {
        let errorCode = error.errorCode ?? ""
        let errorMessage = error.errorMessage ?? ""
        let localized = error.localizedDescription
        print("FinvuManager.fetchLinkedAccounts - Error Code: \(errorCode), " +
              "Error Message: \(errorMessage), " +
              "Localized: \(localized.isEmpty ? "" : localized)")
        return
    }
    // Process linked accounts
    if let accounts = result?.linkedAccounts {
        linkedAccounts = accounts
    }
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'react-native' && <ReactNativeSDKAccountLinking />}
                    </Box>
                </Paper>
            </Box>

            {/* Consent Management Section */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Consent Management
            </Typography>

            <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                After linking accounts, you can manage consent requests by either approving or denying them based on user input.
            </Typography>

            <Box sx={{ mt: 2 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    {/* Platform Selector */}
                    <Box sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.12)',
                        display: 'flex',
                        gap: 1,
                        p: 1
                    }}>
                        {['Android', 'iOS', 'Flutter', 'React-Native'].map((platform) => (
                            <Typography
                                key={platform.toLowerCase()}
                                onClick={() => handlePlatformChange(platform.toLowerCase())}
                                sx={{
                                    px: 3,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem',
                                    fontWeight: selectedPlatform === platform.toLowerCase() ? 600 : 400,
                                    color: selectedPlatform === platform.toLowerCase() ? '#0b3452' : '#666',
                                    bgcolor: selectedPlatform === platform.toLowerCase() ? 'rgba(11, 52, 82, 0.08)' : 'transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: 'rgba(11, 52, 82, 0.05)',
                                        color: '#0b3452'
                                    }
                                }}
                            >
                                {platform}
                            </Typography>
                        ))}
                    </Box>

                    {/* Consent Management Content */}
                    <Box sx={{ p: 2 }}>
                        {selectedPlatform === 'flutter' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    1. Get Consent Request Details
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Retrieves details of consent requests raised by FIU.
                                </Typography>
                                <CopyBlock
                                    text={`// Method signature
Future<FinvuConsentRequestDetailInfo> getConsentRequestDetails(String handleId)

// Usage example
try {
    final consentDetails = await finvuManager.getConsentRequestDetails("HANDLE_ID");
    print('Consent ID: \${consentDetails.consentId}');
    // Process consent request details
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.getConsentRequestDetails - Error Code: $errorCode, Error Message: $errorMessage');
    
    if (errorCode == 'C009' || errorCode == 'F404') {
        showError('Consent request not found.');
    } else if (errorCode == 'C001') {
        showError('Consent has expired. Please request a new consent.');
    } else if (errorCode == 'C010') {
        showError('Consent handle is required.');
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect
        await finvuManager.disconnect();
        await finvuManager.connect();
    } else {
        showError('Failed to fetch consent details: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Approve Consent Request
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Approves consent request for selected accounts.
                                </Typography>
                                <CopyBlock
                                    text={`// Method signature
Future<FinvuProcessConsentRequestResponse> approveConsentRequest(
    FinvuConsentRequestDetailInfo consentInfo,
    List<FinvuLinkedAccountDetailsInfo> linkedAccounts)

// Usage example
try {
    final response = await finvuManager.approveConsentRequest(
        consentInfo: consentRequestDetailInfo,
        linkedAccounts: selectedAccounts,
    );
    print('Consent approved. Consent Intent ID: \${response.consentIntentId}');
    print('Approved \${response.consentInfo.length} consents');
    // Process successful approval
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.approveConsentRequest - Error Code: $errorCode, Error Message: $errorMessage');
    
    if (errorCode == 'C001') {
        showError('Consent has expired. Please fetch updated consent details.');
        // Fetch new consent request details
        final newConsent = await finvuManager.getConsentRequestDetails(
            handleId: consentInfo.consentHandle,
        );
    } else if (errorCode == 'C002') {
        showError('Consent has already been processed.');
    } else if (errorCode == 'C003' || errorCode == 'F404') {
        showError('Consent record not found.');
    } else if (errorCode == 'C005') {
        showError('Invalid or malformed consent. Please check consent details.');
    } else if (errorCode == 'C006') {
        showError('User mismatch. Please login with correct user.');
    } else if (errorCode == 'C007') {
        showError('FIU mismatch. Invalid consent request.');
    } else if (errorCode == 'C008') {
        showError('Invalid linked account. Please select valid accounts.');
    } else if (errorCode == 'C009') {
        showError('Consent handle not found.');
    } else if (errorCode == 'D010') {
        showError('No linked accounts found. Please link accounts first.');
    } else if (errorCode == 'D011') {
        showError('Account reference not found in linked accounts.');
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect
        await finvuManager.disconnect();
        await finvuManager.connect();
    } else {
        showError('Consent approval failed: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. Deny Consent Request
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Denies consent request.
                                </Typography>
                                <CopyBlock
                                    text={`// Method signature
Future<FinvuProcessConsentRequestResponse> denyConsentRequest(
    FinvuConsentRequestDetailInfo consentInfo)

// Usage example
try {
    final response = await finvuManager.denyConsentRequest(
        consentInfo: consentRequestDetailInfo,
    );
    print('Consent denied. Consent Intent ID: \${response.consentIntentId}');
    // Process successful denial
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.denyConsentRequest - Error Code: $errorCode, Error Message: $errorMessage');
    
    if (errorCode == 'C001') {
        showError('Consent has expired.');
    } else if (errorCode == 'C002') {
        showError('Consent has already been processed.');
    } else if (errorCode == 'C003' || errorCode == 'F404') {
        showError('Consent record not found.');
    } else if (errorCode == 'C005') {
        showError('Invalid or malformed consent.');
    } else if (errorCode == 'C006') {
        showError('User mismatch.');
    } else if (errorCode == 'C007') {
        showError('FIU mismatch.');
    } else if (errorCode == 'C009') {
        showError('Consent handle not found.');
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect
        await finvuManager.disconnect();
        await finvuManager.connect();
    } else {
        showError('Consent denial failed: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    4. Get Consent Handle Status
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Checks the status of a specific consent handle to determine if the consent is ACCEPT, DENY, or PENDING. Use this method after consent operations (approve or deny) to verify the final status.
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555', fontStyle: 'italic' }}>
                                    <strong>Note:</strong> This API can only be called a limited number of times (usually 5 times). Exceeding this limit will terminate the websocket session.
                                </Typography>
                                <CopyBlock
                                    text={`// Method signature
Future<FinvuConsentHandleStatusResponse> getConsentHandleStatus(String handleId)

// Usage example
try {
    final statusResponse = await finvuManager.getConsentHandleStatus("HANDLE_ID");
    final status = statusResponse.status;
    print('FinvuManager.getConsentHandleStatus - Success: Status = $status');
    
    switch (status) {
        case "ACCEPT":
            // Consent has been accepted
            print('Consent has been accepted');
            // Process accepted consent
            break;
        case "DENY":
            // Consent has been denied
            print('Consent has been denied');
            // Process denied consent
            break;
        case "PENDING":
            // Consent is still pending
            print('Consent is still pending');
            // Handle pending status - may need to poll again later
            break;
        default:
            // Unknown status
            print('Unknown status: $status');
    }
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.getConsentHandleStatus - Error Code: $errorCode, Error Message: $errorMessage');
    
    if (errorCode == 'C009' || errorCode == 'F404') {
        showError('Consent handle not found.');
    } else if (errorCode == 'C010') {
        showError('Consent handle is required.');
    } else if (errorCode == 'F429') {
        showError('Maximum polling attempts exceeded. Please check consent status later.');
        // Session may be terminated - reconnect if needed
        await finvuManager.disconnect();
        await finvuManager.connect();
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect
        await finvuManager.disconnect();
        await finvuManager.connect();
    } else {
        showError('Failed to get consent status: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'android' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    1. Get Consent Request Details
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Retrieves details of consent requests raised by FIU.
                                </Typography>
                                <CopyBlock
                                    text={`finvuManager.getConsentRequestDetails(handleId) { result ->
    runOnUiThread {
        if (result.isSuccess) {
            val consentRequestDetails = result.getOrNull()
        // Process consent request details
        } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Get consent request details failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
        }
    }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Approve Consent Request
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Approves consent request for selected accounts.
                                </Typography>
                                <CopyBlock
                                    text={`finvuManager.approveConsentRequest(
    consentInfo = consentRequestDetailInfo,
    linkedAccounts = selectedAccounts
) { result ->
        runOnUiThread {
            if (result.isSuccess) {
               // Process successful approval
        } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Approve consent request failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
            }
        }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. Deny Consent Request
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Denies consent request.
                                </Typography>
                                <CopyBlock
                                    text={`finvuManager.denyConsentRequest(
    consentInfo = consentRequestDetailInfo
) { result ->
        runOnUiThread {
            if (result.isSuccess) {
            // Process successful denial
        } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Deny consent request failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
        }
    }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    4. Get Consent Handle Status
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Checks the status of a specific consent handle. The status can be <code>ACCEPT</code>, <code>DENY</code>, or <code>PENDING</code>.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.getConsentHandleStatus(
    handleId = handleId
) { result ->
    runOnUiThread {
        if (result.isSuccess) {
            val consentHandleStatusResponse = result.getOrNull()
            val status = consentHandleStatusResponse?.status
            Log.d("ConsentStatus", "Consent handle status: \${status}")
            
            when (status) {
                "ACCEPT" -> {
                    // Consent has been accepted
                    // Process accepted consent
                }
                "DENY" -> {
                    // Consent has been denied
                    // Process denied consent
                }
                "PENDING" -> {
                    // Consent is still pending
                    // Handle pending status
                }
                else -> {
                    // Unknown status
                    Log.w("ConsentStatus", "Unknown consent status: \${status}")
                }
            }
        } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Get consent handle status failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
            }
        }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'ios' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    1. Get Consent Request Details
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Retrieves details of consent requests raised by FIU.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.getConsentRequestDetails(
    handleId: handleId
) { response, error in
    if let error = error {
        let errorCode = error.errorCode ?? ""
        let errorMessage = error.errorMessage ?? ""
        let localized = error.localizedDescription
        print("FinvuManager.getConsentRequestDetails - Error Code: \(errorCode), " +
              "Error Message: \(errorMessage), " +
              "Localized: \(localized.isEmpty ? "" : localized)")
        return
    }
    // Process consent request details
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    2. Approve Consent Request
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Approves consent request for selected accounts.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.approveAccountConsentRequest(
    consentInfo: consentRequestDetailInfo,
    linkedAccounts: selectedAccounts
) { response, error in
    if let error = error {
        let errorCode = error.errorCode ?? ""
        let errorMessage = error.errorMessage ?? ""
        let localized = error.localizedDescription
        print("FinvuManager.approveAccountConsentRequest - Error Code: \(errorCode), " +
              "Error Message: \(errorMessage), " +
              "Localized: \(localized.isEmpty ? "" : localized)")
        return
    }
    // Process successful approval
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    3. Deny Consent Request
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Denies consent request.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.denyAccountConsentRequest(
    consentInfo: consentRequestDetailInfo
) { response, error in
    if let error = error {
        let errorCode = error.errorCode ?? ""
        let errorMessage = error.errorMessage ?? ""
        let localized = error.localizedDescription
        print("FinvuManager.denyAccountConsentRequest - Error Code: \(errorCode), " +
              "Error Message: \(errorMessage), " +
              "Localized: \(localized.isEmpty ? "" : localized)")
        return
    }
    // Process successful denial
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 2 }}>
                                    4. Get Consent Handle Status
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Checks the status of a specific consent handle to determine if the consent is <code>ACCEPT</code>, <code>DENY</code>, or <code>PENDING</code>. Use this method after consent operations (approve or deny) to verify the final status and track consent state changes.
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.getConsentHandleStatus(
    handleId: handleId
) { response, error in
    if let error = error {
        let errorCode = error.errorCode ?? ""
        let errorMessage = error.errorMessage ?? ""
        let localized = error.localizedDescription
        print("FinvuManager.getConsentHandleStatus - Error Code: \(errorCode), " +
              "Error Message: \(errorMessage), " +
              "Localized: \(localized.isEmpty ? "" : localized)")
        return
    }
    
    if let response = response {
        let status = response.status
        print("FinvuManager.getConsentHandleStatus - Success: Status = \(status)")
        
        switch status {
        case "ACCEPT":
            // Consent has been accepted
            // Process accepted consent
            break
        case "DENY":
            // Consent has been denied
            // Process denied consent
            break
        case "PENDING":
            // Consent is still pending
            // Handle pending status
            break
        default:
            // Unknown status
            print("FinvuManager.getConsentHandleStatus - Unknown status: \(status)")
        }
    } else {
        print("FinvuManager.getConsentHandleStatus - Success: No response data")
    }
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'react-native' && <ReactNativeSDKConsentManagement />}
                    </Box>
                </Paper>
            </Box>

            {/* Revoke Consent Section */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Revoke Consent
            </Typography>

            <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                The revoke consent functionality allows users to revoke previously granted consents. This is useful when users want to withdraw their consent for data sharing with Financial Information Users (FIUs).
            </Typography>

            <Box sx={{ mt: 2 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    {/* Platform Selector */}
                    <Box sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.12)',
                        display: 'flex',
                        gap: 1,
                        p: 1
                    }}>
                        {['Android', 'iOS', 'Flutter', 'React-Native'].map((platform) => (
                            <Typography
                                key={platform.toLowerCase()}
                                onClick={() => handlePlatformChange(platform.toLowerCase())}
                                sx={{
                                    px: 3,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem',
                                    fontWeight: selectedPlatform === platform.toLowerCase() ? 600 : 400,
                                    color: selectedPlatform === platform.toLowerCase() ? '#0b3452' : '#666',
                                    bgcolor: selectedPlatform === platform.toLowerCase() ? 'rgba(11, 52, 82, 0.08)' : 'transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: 'rgba(11, 52, 82, 0.05)',
                                        color: '#0b3452'
                                    }
                                }}
                            >
                                {platform}
                            </Typography>
                        ))}
                    </Box>

                    {/* Revoke Consent Content */}
                    <Box sx={{ p: 2 }}>
                        {selectedPlatform === 'android' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    Revoke Consent
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Revokes a previously granted consent using the consent ID. The optional parameters <code>accountAggregatorView</code> and <code>fipDetails</code> can be set to <code>null</code> if not required.
                                </Typography>
                                <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                                    📥 Parameters:
                                </Typography>
                                <ul>
                                    <li><code>consentId</code>: <i>string</i> — The ID of the consent to revoke (required)</li>
                                    <li><code>accountAggregatorView</code>: <i>AccountAggregatorView?</i> — Optional account aggregator view (can be null)</li>
                                    <li><code>fipDetails</code>: <i>FIPReferenceView?</i> — Optional FIP reference details (can be null)</li>
                                </ul>
                                <Typography sx={{ fontWeight: 'bold', mt: 2 }}>
                                    ✅ Response Type: <code>Result&lt;Unit&gt;</code>
                                </Typography>
                                <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 2 }}>
                                    Success: Returns <code>Result.success(Unit)</code><br />
                                    Error: Returns <code>Result.failure(FinvuException)</code>
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.revokeConsent(
    consentId = consentId,
    accountAggregatorView = null,
    fipDetails = null
) { result ->
    runOnUiThread {
        if (result.isSuccess) {
        // Consent revoked successfully
        } else {
            val exception = result.exceptionOrNull() as? FinvuException
            Log.e("FinvuError", "❌ ActivityName - Revoke consent failed - Code: \${exception?.code}, Message: \${exception?.message}")
            Toast.makeText(this, "Something went wrong!!", Toast.LENGTH_SHORT).show()
        }
    }
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'ios' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    Revoke Consent
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Revokes a previously granted consent using the consent ID. The optional parameters <code>accountAggregator</code> and <code>fipDetails</code> can be set to <code>nil</code> if not required.
                                </Typography>
                                <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                                    📥 Parameters:
                                </Typography>
                                <ul>
                                    <li><code>consentId</code>: <i>String</i> — The ID of the consent to revoke (required)</li>
                                    <li><code>accountAggregator</code>: <i>AccountAggregator?</i> — Optional account aggregator (can be nil)</li>
                                    <li><code>fipDetails</code>: <i>FIPReference?</i> — Optional FIP reference details (can be nil)</li>
                                </ul>
                                <Typography sx={{ fontWeight: 'bold', mt: 2 }}>
                                    ✅ Response Type: <code>NSError?</code>
                                </Typography>
                                <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 2 }}>
                                    Success: Returns <code>nil</code><br />
                                    Error: Returns <code>NSError</code> with error details
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.revokeConsent(
    consentId: consentId,
    accountAggregator: nil,
    fipDetails: nil
) { error in
    if let error = error {
        let errorCode = error.errorCode ?? ""
        let errorMessage = error.errorMessage ?? ""
        let localized = error.localizedDescription
        print("FinvuManager.revokeConsent - Error Code: \(errorCode), " +
              "Error Message: \(errorMessage), " +
              "Localized: \(localized.isEmpty ? "" : localized)")
        return
    }
        // Consent revoked successfully
        print("Consent revoked successfully")
}`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'flutter' && (
                            <>
                                <Typography sx={{ mb: 2 }}>
                                    Revoke Consent
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    Revokes a previously granted consent using the consent ID. The optional parameters <code>accountAggregator</code> and <code>fipDetails</code> can be set to <code>null</code> if not required.
                                </Typography>
                                <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                                    📥 Parameters:
                                </Typography>
                                <ul>
                                    <li><code>consentId</code>: <i>String</i> — The ID of the consent to revoke (required)</li>
                                    <li><code>accountAggregator</code>: <i>AccountAggregator?</i> — Optional account aggregator (can be null)</li>
                                    <li><code>fipDetails</code>: <i>FIPReference?</i> — Optional FIP reference details (can be null)</li>
                                </ul>
                                <Typography sx={{ fontWeight: 'bold', mt: 2 }}>
                                    ✅ Response Type: <code>Future&lt;void&gt;</code>
                                </Typography>
                                <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 2 }}>
                                    Success: Completes without error<br />
                                    Error: Throws <code>FinvuException</code>
                                </Typography>
                                <CopyBlock
                                    text={`try {
    await finvuManager.revokeConsent(
        consentId,
        accountAggregator: null, // Optional
        fipDetails: null, // Optional
    );
    print('Consent revoked successfully');
} on FinvuException catch (e) {
    final errorCode = e.code;
    final errorMessage = e.message ?? '';
    print('FinvuManager.revokeConsent - Error Code: $errorCode, Error Message: $errorMessage');
    
    if (errorCode == 'C003' || errorCode == 'F404') {
        showError('Consent not found.');
    } else if (errorCode == 'C002') {
        showError('Consent has already been processed.');
    } else if (errorCode == 'C001') {
        showError('Consent has expired.');
    } else if (errorCode == 'F402' || errorCode == 'A002') {
        // Session error - reconnect
        await finvuManager.disconnect();
        await finvuManager.connect();
    } else {
        showError('Consent revocation failed: \${errorMessage.isEmpty ? 'Please try again' : errorMessage}');
    }
} catch (e) {
    print('Unexpected error: $e');
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'react-native' && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography sx={{ color: '#555', fontStyle: 'italic' }}>
                                    Revoke Consent functionality for React Native SDK is coming soon. Please check back for updates.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>

            {/* Error Codes Section */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                FinvuSDK Error Codes
            </Typography>

            <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                The SDK provides specific error codes to help you handle different types of errors appropriately. These error codes are categorized into two types:
            </Typography>

            <Typography sx={{ mt: 2, mb: 1, color: "#0b3452", fontWeight: "bold" }}>
                1. Backend Error Codes
            </Typography>
            <Typography sx={{ mb: 2, color: '#555', fontSize: '0.95rem' }}>
                These error codes are returned directly from the backend API.
            </Typography>

            <BackendResponseCodeMapping />

            <Typography sx={{ mt: 4, mb: 1, color: "#0b3452", fontWeight: "bold" }}>
                2. SDK Error Codes
            </Typography>
            <Typography sx={{ mb: 2, color: '#555', fontSize: '0.95rem' }}>
                These error codes are generated by the SDK itself when the backend returns a null error code or for SDK-specific errors. If the backend returns a null error code, the SDK will use these error codes instead.
            </Typography>

            <Box sx={{ mt: 2 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Authentication Errors */}
                            <Box>
                                <Typography sx={{ mb: 1 }}>
                                    <code>AUTH_LOGIN_RETRY</code> (Code: 1001)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555', fontSize: '0.95rem' }}>
                                    Triggered when the login attempt fails, and the system retries the login process.
                                </Typography>

                                <Typography sx={{ mb: 1 }}>
                                    <code>AUTH_LOGIN_FAILED</code> (Code: 1002)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555', fontSize: '0.95rem' }}>
                                    Occurs when the login attempt fails permanently (incorrect credentials, etc.).
                                </Typography>

                                <Typography sx={{ mb: 1 }}>
                                    <code>AUTH_FORGOT_PASSWORD_FAILED</code> (Code: 1003)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555', fontSize: '0.95rem' }}>
                                    Raised when the request to reset the password fails.
                                </Typography>

                                <Typography sx={{ mb: 1 }}>
                                    <code>AUTH_LOGIN_VERIFY_MOBILE_NUMBER</code> (Code: 1004)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555', fontSize: '0.95rem' }}>
                                    Indicates the need to verify the mobile number during the login process.
                                </Typography>

                                <Typography sx={{ mb: 1 }}>
                                    <code>AUTH_FORGOT_HANDLE_FAILED</code> (Code: 1005)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555', fontSize: '0.95rem' }}>
                                    Raised when the handle (email/username) for the forgot password process fails.
                                </Typography>

                                <Typography sx={{ mb: 1 }}>
                                    <code>SESSION_DISCONNECTED</code> (Code: 8000)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555', fontSize: '0.95rem' }}>
                                    Occurs when the user session is unexpectedly disconnected.
                                </Typography>

                                <Typography sx={{ mb: 1 }}>
                                    <code>SSL_PINNING_FAILURE_ERROR</code> (Code: 8001)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555', fontSize: '0.95rem' }}>
                                    Triggered when SSL pinning fails during the network request.
                                </Typography>

                                <Typography sx={{ mb: 1 }}>
                                    <code>RECORD_NOT_FOUND</code> (Code: 8002)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555', fontSize: '0.95rem' }}>
                                    Raised when the requested record is not found in the database.
                                </Typography>

                                <Typography sx={{ mb: 1 }}>
                                    <code>LOGOUT</code> (Code: 9000)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555', fontSize: '0.95rem' }}>
                                    Triggered when the user successfully logs out of the application.
                                </Typography>

                                <Typography sx={{ mb: 1 }}>
                                    <code>GENERIC_ERROR</code> (Code: 9999)
                                </Typography>
                                <Typography sx={{ mb: 2, color: '#555', fontSize: '0.95rem' }}>
                                    A catch-all error for unspecified issues or unhandled exceptions.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Box>

            {/* Error Handling Examples Section */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Error Handling Examples
            </Typography>

            <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                Below are platform-specific error handling examples for Flutter and React Native SDKs. These examples demonstrate how to handle errors based on error codes.
            </Typography>

            <Box sx={{ mt: 2 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    {/* Platform Selector */}
                    <Box sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.12)',
                        display: 'flex',
                        gap: 1,
                        p: 1
                    }}>
                        {['Flutter', 'React-Native'].map((platform) => (
                            <Typography
                                key={platform.toLowerCase()}
                                onClick={() => handlePlatformChange(platform.toLowerCase())}
                                sx={{
                                    px: 3,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem',
                                    fontWeight: selectedPlatform === platform.toLowerCase() ? 600 : 400,
                                    color: selectedPlatform === platform.toLowerCase() ? '#0b3452' : '#666',
                                    bgcolor: selectedPlatform === platform.toLowerCase() ? 'rgba(11, 52, 82, 0.08)' : 'transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: 'rgba(11, 52, 82, 0.05)',
                                        color: '#0b3452'
                                    }
                                }}
                            >
                                {platform}
                            </Typography>
                        ))}
                    </Box>

                    {/* Error Handling Content */}
                    <Box sx={{ p: 2 }}>
                        {selectedPlatform === 'flutter' && (
                            <>
                                <Typography sx={{ mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                                    Error Handling in Flutter
                                </Typography>
                                
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    The Flutter SDK uses <code>FinvuException</code> for error handling. All SDK methods that can fail throw <code>FinvuException</code>. Use try-catch blocks to handle errors:
                                </Typography>

                                <Typography sx={{ mt: 3, mb: 1, color: "#0b3452", fontWeight: "bold" }}>
                                    Basic Error Handling Pattern
                                </Typography>
                                <CopyBlock
                                    text={`try {
  await finvuManager.connect();
  print("Connected successfully");
} on FinvuException catch (e) {
  print("Error Code: \${e.code}");
  print("Error Message: \${e.message ?? 'No message'}");
  // Handle error appropriately
} catch (e) {
  print("Unexpected error: \$e");
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 1, color: "#0b3452", fontWeight: "bold" }}>
                                    Handling Specific Error Codes
                                </Typography>
                                <CopyBlock
                                    text={`try {
  await finvuManager.verifyLoginOtp(
    otp: "123456",
    otpReference: otpReference.reference,
  );
} on FinvuException catch (e) {
  switch (e.code) {
    case 'A005':
      // Invalid OTP
      showError("Invalid OTP. Please try again.");
      break;
    case 'A006':
      // Invalid OTP reference - restart login flow
      showError("Session expired. Please login again.");
      restartLoginFlow();
      break;
    case 'F402':
      // Session error - reconnect
      await handleSessionError();
      break;
    default:
      // Handle generic error
      showError(e.message ?? 'An error occurred');
  }
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 1, color: "#0b3452", fontWeight: "bold" }}>
                                    Connection Error Handling
                                </Typography>
                                <CopyBlock
                                    text={`Future<void> ensureConnected() async {
  try {
    if (!await finvuManager.isConnected()) {
      await finvuManager.connect();
    }
  } on FinvuException catch (e) {
    if (e.code == '8000' || e.code == 'F402') {
      // Retry connection
      await Future.delayed(Duration(seconds: 2));
      await finvuManager.connect();
    } else {
      rethrow;
    }
  }
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 1, color: "#0b3452", fontWeight: "bold" }}>
                                    Session Error Handling with Retry
                                </Typography>
                                <CopyBlock
                                    text={`Future<T> executeWithRetry<T>(Future<T> Function() operation) async {
  try {
    return await operation();
  } on FinvuException catch (e) {
    if (e.code == 'F402') {
      // Session error - reconnect and retry
      await finvuManager.disconnect();
      await finvuManager.connect();
      return await operation(); // Retry once
    }
    rethrow;
  }
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 1, color: "#0b3452", fontWeight: "bold" }}>
                                    Complete Login Flow with Error Handling
                                </Typography>
                                <CopyBlock
                                    text={`Future<void> loginUser() async {
  try {
    // Step 1: Connect
    await finvuManager.connect();
    
    // Step 2: Login with handle
    final otpReference = await finvuManager
        .loginWithUsernameOrMobileNumberAndConsentHandle(
      username: "user@finvu",
      mobileNumber: "9876543210",
      consentHandleId: "handle_id",
    );
    
    // Step 3: Verify OTP
    final handleInfo = await finvuManager.verifyLoginOtp(
      otp: "123456",
      otpReference: otpReference.reference,
    );
    
    print("Login successful: \${handleInfo.userId}");
    
  } on FinvuException catch (e) {
    // Handle SDK errors
    if (e.code == 'A005') {
      // Invalid OTP
      showError("Invalid OTP. Please try again.");
    } else if (e.code == 'F402') {
      // Session error - reconnect
      await finvuManager.connect();
      // Retry operation
    } else {
      // Generic error
      showError(e.message ?? "An error occurred: \${e.code}");
    }
  } catch (e) {
    // Handle unexpected errors
    print("Unexpected error: \$e");
    showError("An unexpected error occurred");
  }
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}

                        {selectedPlatform === 'react-native' && (
                            <>
                                <Typography sx={{ mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                                    Error Handling in React Native
                                </Typography>
                                
                                <Typography sx={{ mb: 2, color: '#555' }}>
                                    The React Native SDK uses a <code>Result&lt;T&gt;</code> pattern for error handling. All SDK methods return a <code>Result</code> that can be either success or error:
                                </Typography>

                                <Typography sx={{ mt: 3, mb: 1, color: "#0b3452", fontWeight: "bold" }}>
                                    Basic Error Handling Pattern
                                </Typography>
                                <CopyBlock
                                    text={`import * as Finvu from 'finvu-react-native-sdk';

const result = await Finvu.someMethod();

if (result.isSuccess) {
  // Handle success
  const data = result.data;
  // Use data...
} else {
  // Handle error
  const errorCode = result.error.code;
  const errorMessage = result.error.message;
  
  // Handle specific error codes
  switch (errorCode) {
    case 'F401':
      // Handle unauthorized
      break;
    case 'A005':
      // Handle invalid OTP
      break;
    default:
      // Handle generic error
      break;
  }
}`}
                                    language="typescript"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 1, color: "#0b3452", fontWeight: "bold" }}>
                                    Connection Error Handling
                                </Typography>
                                <CopyBlock
                                    text={`const connectResult = await Finvu.connect();

if (connectResult.isSuccess) {
  console.log("Connected successfully");
} else {
  const errorCode = connectResult.error.code;
  const errorMessage = connectResult.error.message;
  
  switch (errorCode) {
    case '8000':
      // Session disconnected - retry connection
      await new Promise(resolve => setTimeout(resolve, 2000));
      await Finvu.connect();
      break;
    case '8001':
      // SSL pinning failure
      console.error("SSL certificate validation failed");
      break;
    case '9999':
      // Generic error
      console.error("Connection failed:", errorMessage);
      break;
  }
}`}
                                    language="typescript"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 1, color: "#0b3452", fontWeight: "bold" }}>
                                    Login Flow with Error Handling
                                </Typography>
                                <CopyBlock
                                    text={`// Step 1: Login
const loginResult = await Finvu.loginWithUsernameOrMobileNumber(
  "9309107496@finvu",
  "9309107496",
  "consent_handle_id"
);

if (!loginResult.isSuccess) {
  const errorCode = loginResult.error.code;
  
  switch (errorCode) {
    case 'A001':
      console.error("Invalid login user format");
      break;
    case 'A003':
      console.error("OTP initiation failed");
      break;
    case 'F401':
      console.error("Unauthorized");
      break;
    default:
      console.error("Login failed:", loginResult.error.message);
  }
  return;
}

const otpReference = loginResult.data.reference;

// Step 2: Verify OTP
const verifyResult = await Finvu.verifyLoginOtp("123456", otpReference);

if (!verifyResult.isSuccess) {
  const errorCode = verifyResult.error.code;
  
  switch (errorCode) {
    case 'A004':
      console.error("Login OTP verification failed");
      break;
    case 'A005':
      console.error("Invalid OTP");
      // Allow user to retry
      break;
    case 'A006':
      console.error("Invalid OTP Reference");
      // Restart login flow
      break;
    case 'F429':
      console.error("Max attempts exceeded");
      break;
    default:
      console.error("OTP verification failed:", verifyResult.error.message);
  }
  return;
}

const userId = verifyResult.data.userId;
console.log("Login successful. User ID:", userId);`}
                                    language="typescript"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 1, color: "#0b3452", fontWeight: "bold" }}>
                                    Account Discovery with Error Handling
                                </Typography>
                                <CopyBlock
                                    text={`const discoverResult = await Finvu.discoverAccounts(
  "FIP_ID_123",
  ["DEPOSIT", "RECURRING_DEPOSIT"],
  [
    {
      category: "STRONG",
      type: "MOBILE",
      value: "9309107496"
    }
  ]
);

if (!discoverResult.isSuccess) {
  const errorCode = discoverResult.error.code;
  
  switch (errorCode) {
    case 'D001':
      console.error("User ID mismatch");
      // Re-login required
      break;
    case 'D002':
      console.error("Unsupported financial information type");
      break;
    case 'D003':
      console.error("Device not authenticated");
      break;
    case 'D007':
      console.error("Invalid FIP");
      break;
    case 'D008':
      console.error("FIP operation failed");
      // May need to retry or wait
      break;
    default:
      console.error("Discovery failed:", discoverResult.error.message);
  }
  return;
}

const accounts = discoverResult.data.discoveredAccounts;
console.log(\`Discovered \${accounts.length} accounts\`);`}
                                    language="typescript"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 1, color: "#0b3452", fontWeight: "bold" }}>
                                    Consent Management with Error Handling
                                </Typography>
                                <CopyBlock
                                    text={`// Get consent request details
const consentResult = await Finvu.getConsentRequestDetails("consent_handle_123");

if (!consentResult.isSuccess) {
  const errorCode = consentResult.error.code;
  
  switch (errorCode) {
    case 'C003':
      console.error("Record not found");
      break;
    case 'C009':
      console.error("Consent handle not found");
      break;
    default:
      console.error("Failed to get consent details:", consentResult.error.message);
  }
  return;
}

const consentDetail = consentResult.data;

// Approve consent
const approveResult = await Finvu.approveConsentRequest(
  consentDetail,
  linkedAccounts
);

if (!approveResult.isSuccess) {
  const errorCode = approveResult.error.code;
  
  switch (errorCode) {
    case 'C001':
      console.error("Consent expired");
      // Fetch new consent request
      break;
    case 'C002':
      console.error("Consent already actioned");
      break;
    case 'C005':
      console.error("Invalid or malformed consent");
      break;
    case 'C006':
      console.error("User mismatch");
      break;
    case 'C007':
      console.error("FIU mismatch");
      break;
    case 'C008':
      console.error("Invalid linked account");
      break;
    default:
      console.error("Failed to approve consent:", approveResult.error.message);
  }
  return;
}

console.log("Consent approved successfully");`}
                                    language="typescript"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ mt: 3, mb: 1, color: "#0b3452", fontWeight: "bold" }}>
                                    Helper Function for Error Handling
                                </Typography>
                                <CopyBlock
                                    text={`async function handleSDKError<T>(
  operation: () => Promise<Finvu.Result<T>>,
  onSuccess?: (data: T) => void,
  onError?: (error: { code: string; message: string }) => void
): Promise<T | null> {
  const result = await operation();
  
  if (result.isSuccess) {
    if (onSuccess) {
      onSuccess(result.data);
    }
    return result.data;
  } else {
    const error = result.error;
    
    // Log error
    console.error(\`SDK Error - Code: \${error.code}, Message: \${error.message}\`);
    
    // Handle specific error codes
    switch (error.code) {
      case 'F402':
      case 'A002':
        // Session error - reconnect
        await Finvu.disconnect();
        await Finvu.connect();
        // Optionally retry operation
        break;
      case '8000':
        // Session disconnected - reconnect
        await Finvu.connect();
        break;
    }
    
    if (onError) {
      onError(error);
    }
    
    return null;
  }
}

// Usage
const accounts = await handleSDKError(
  () => Finvu.fetchLinkedAccounts(),
  (data) => console.log(\`Fetched \${data.linkedAccounts.length} accounts\`),
  (error) => console.error("Failed to fetch accounts:", error.message)
);`}
                                    language="typescript"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                    </Box>
                </Paper>
            </Box>

            {/* FAQ Section */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Frequently Asked Questions
            </Typography>

            <Box sx={{ mt: 3, mb: 4 }}>
                <Paper sx={{
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    {/* Platform Selector */}
                    <Box sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.12)',
                        display: 'flex',
                        gap: 1,
                        p: 1
                    }}>
                        {['Android', 'iOS', 'Flutter', 'React-Native'].map((platform) => (
                            <Typography
                                key={platform.toLowerCase()}
                                onClick={() => handlePlatformChange(platform.toLowerCase())}
                                sx={{
                                    px: 3,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem',
                                    fontWeight: selectedPlatform === platform.toLowerCase() ? 600 : 400,
                                    color: selectedPlatform === platform.toLowerCase() ? '#0b3452' : '#666',
                                    bgcolor: selectedPlatform === platform.toLowerCase() ? 'rgba(11, 52, 82, 0.08)' : 'transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: 'rgba(11, 52, 82, 0.05)',
                                        color: '#0b3452'
                                    }
                                }}
                            >
                                {platform}
                            </Typography>
                        ))}
                    </Box>

                    {/* FAQ Content */}
                    <Box sx={{ p: 2 }}>
                        {selectedPlatform === 'android' && (
                            <>
                                <Typography sx={{ fontWeight: 'bold', color: '#0b3452' }}>
                                    Q: I'm getting a Kotlin version compatibility error. How do I fix it?
                                </Typography>
                                <Typography sx={{ mt: 1, mb: 2, color: '#555' }}>
                                    A: The error "Class was compiled with an incompatible version of Kotlin" occurs when your Kotlin version doesn't match the SDK's requirements. Ensure that in your <code>settings.gradle</code> file, the Kotlin version is set to 1.9.0:
                                </Typography>
                                <CopyBlock
                                    text={`plugins {
    id "org.jetbrains.kotlin.android" version "1.9.0" apply false
}`}
                                    language="groovy"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ fontWeight: 'bold', color: '#0b3452', mt: 4 }}>
                                    Q: How do I set up GitHub authentication for the SDK?
                                </Typography>
                                <Typography sx={{ mt: 1, mb: 2, color: '#555' }}>
                                    A: You'll need to set up GitHub Personal Access Token (PAT) with package read permissions. Add these to your environment variables or gradle.properties:
                                </Typography>
                                <CopyBlock
                                    text={`GITHUB_PACKAGE_USERNAME=your_github_username
GITHUB_PACKAGE_TOKEN=your_github_pat`}
                                    language="properties"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ fontWeight: 'bold', color: '#0b3452', mt: 4 }}>
                                    Q: How do I handle WebSocket connection errors?
                                </Typography>
                                <Typography sx={{ mt: 1, mb: 2, color: '#555' }}>
                                    A: The SDK provides specific error codes through FinvuException. Implement error handling based on these codes:
                                </Typography>
                                <CopyBlock
                                    text={`when (error.code) {
    CONNECTION_ERROR -> // Handle connection issues
    TIMEOUT_ERROR -> // Handle timeout
    AUTH_ERROR -> // Handle authentication issues
}`}
                                    language="kotlin"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'ios' && (
                            <>
                                <Typography sx={{ fontWeight: 'bold', color: '#0b3452' }}>
                                    Q: How do I handle SSL/TLS certificate validation?
                                </Typography>
                                <Typography sx={{ mt: 1, mb: 2, color: '#555' }}>
                                    A: The SDK handles certificate validation by default. For development environments, you may need to configure ATS settings in Info.plist.
                                </Typography>
                                <CopyBlock
                                    text={`<key>NSAppTransportSecurity</key>
<dict>
    <key>NSAllowsArbitraryLoads</key>
    <true/>
</dict>`}
                                    language="xml"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ fontWeight: 'bold', color: '#0b3452', mt: 4 }}>
                                    Q: How do I debug network issues?
                                </Typography>
                                <Typography sx={{ mt: 1, mb: 2, color: '#555' }}>
                                    A: Enable SDK logging by setting the log level in your configuration:
                                </Typography>
                                <CopyBlock
                                    text={`FinvuManager.shared.setLogLevel(.debug)`}
                                    language="swift"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'flutter' && (
                            <>
                                <Typography sx={{ fontWeight: 'bold', color: '#0b3452' }}>
                                    Q: How do I handle SDK initialization errors?
                                </Typography>
                                <Typography sx={{ mt: 1, mb: 2, color: '#555' }}>
                                    A: Wrap the initialization in a try-catch block and handle specific error types:
                                </Typography>
                                <CopyBlock
                                    text={`try {
    await finvuManager.initialize(config);
} on PlatformException catch (e) {
    // Handle platform-specific errors
} on FinvuException catch (e) {
    // Handle SDK-specific errors
}`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />

                                <Typography sx={{ fontWeight: 'bold', color: '#0b3452', mt: 4 }}>
                                    Q: How do I debug platform channel issues?
                                </Typography>
                                <Typography sx={{ mt: 1, mb: 2, color: '#555' }}>
                                    A: Enable verbose logging in your Flutter app and check platform-specific logs:
                                </Typography>
                                <CopyBlock
                                    text={`// Enable verbose logging
if (kDebugMode) {
    debugPrint('[FinvuSDK] Debug message');
}

// Check platform logs:
// Android: adb logcat | grep "FinvuSDK"
// iOS: Console.app -> search "FinvuSDK"`}
                                    language="dart"
                                    showLineNumbers={true}
                                    theme={getThemeForPlatform(selectedPlatform)}
                                />
                            </>
                        )}
                        {selectedPlatform === 'react-native' && (
                            <>
                                <ReactNativeSDKFAQSection />
                                <ReactNativeSDKScriptURLLoadingFAQ />
                            </>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );

    const EventTrackingContent = () => {
        const eventTrackingComponents: Record<string, React.FC> = {
            'android': AndroidEventTracking,
            'ios': iOSEventTracking,
            'flutter': FlutterEventTracking,
            'react-native': ReactNativeEventTracking,
        };

        const SelectedComponent = eventTrackingComponents[selectedEventPlatform] || AndroidEventTracking;

        return (
            <Box>
                <Typography sx={{ mt: 2, mb: 4, color: "#555", lineHeight: 1.7 }}>
                    The Finvu Mobile SDK provides comprehensive event tracking capabilities to monitor user journeys, 
                    track custom events, and integrate with analytics systems across Android, iOS, Flutter, and React Native platforms.
                </Typography>

                {/* Platform Selector */}
                <Box sx={{ mt: 3, mb: 4 }}>
                    <Paper sx={{
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        {/* Platform Selector */}
                        <Box sx={{
                            borderBottom: '1px solid rgba(0,0,0,0.12)',
                            display: 'flex',
                            gap: 1,
                            p: 1
                        }}>
                            {['Android', 'iOS', 'Flutter', 'React-Native'].map((platform) => (
                                <Typography
                                    key={platform.toLowerCase()}
                                    onClick={() => handleEventPlatformChange(platform.toLowerCase())}
                                    sx={{
                                        px: 3,
                                        py: 1.5,
                                        cursor: 'pointer',
                                        borderRadius: '4px',
                                        fontSize: '0.95rem',
                                        fontWeight: selectedEventPlatform === platform.toLowerCase() ? 600 : 400,
                                        color: selectedEventPlatform === platform.toLowerCase() ? '#0b3452' : '#666',
                                        bgcolor: selectedEventPlatform === platform.toLowerCase() ? 'rgba(11, 52, 82, 0.08)' : 'transparent',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: 'rgba(11, 52, 82, 0.05)',
                                            color: '#0b3452'
                                        }
                                    }}
                                >
                                    {platform}
                                </Typography>
                            ))}
                        </Box>

                        {/* Event Tracking Content */}
                        <Box sx={{ p: 3 }}>
                            <SelectedComponent />
                        </Box>
                    </Paper>
                </Box>
            </Box>
        );
    };

    const getPrevTabPath = (currentTab: number) => {
        if (currentTab === 0) {
            return "/web-sdk-integration#sdk";
        }
        return "/mobile-sdk-integration#sdk";
    };

    const getNextTabPath = (currentTab: number) => {
        if (currentTab === 0) {
            return "/devops-guidelines";
        }
        return "/web-sdk-integration#sdk";
    };

    const TabNavigation = ({ currentTab }: { currentTab: number }) => (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 6,
                mb: 2,
                borderTop: "1px solid #e0e0e0",
                pt: 4,
            }}
        >
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(getPrevTabPath(currentTab))}
                sx={{ color: "#0b3452" }}
            >
                {currentTab === 1 ? "SDK Integration" : "Web SDK Integration"}
            </Button>
            <Button
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate(getNextTabPath(currentTab))}
                sx={{ color: "#0b3452" }}
            >
                {currentTab === 0 ? "Co-Development Guidelines" : "Web SDK Integration"}
            </Button>
        </Box>
    );

    return (
        <Box sx={{ paddingLeft: 4 }}>
            <Typography
                sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: "1.2rem", sm: "1.5rem" },
                    color: "#0b3452",
                    display: 'inline-block',
                    marginBottom: 2,
                    lineHeight: 1.5,
                }}
            >
                Mobile SDK Integration
            </Typography>

            <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    marginBottom: 3,
                }}
            >
                <Tab label="SDK Integration" />
                <Tab label="Event Tracking" />
            </Tabs>

            {selectedTab === 0 ? <SDKContent /> : <EventTrackingContent />}

            <TabNavigation currentTab={selectedTab} />
        </Box>
    );
});

export default MobileSDKIntegration;
